import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { Upload, X, Plus, Loader2, ArrowLeft, ArrowRight, MapPin, Navigation, Clock, DollarSign, Percent, Gavel, Tag, Package, Trash2, CalendarClock, Car, Anchor, Sofa, AlertCircle, FileText, Wrench, Home, Key, Bed, Bath, Maximize, ParkingCircle, Globe, Check, Star, GripVertical, Save, CheckCircle, Video, Play, Camera, MessageCircle, ShoppingBag, Repeat, Briefcase, Image, Phone, Mail, Eye, ChevronDown, Circle, Link2, Sparkles, Shield, Pencil } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API, useAuth, useLanguage, useCurrency, useIsland } from '../App';
import Navbar from '../components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { SafariDropdown } from '../components/ui/safari-dropdown';
import LocationPicker from '../components/LocationPicker';
import { safeClick } from '../utils/safeClick';
import ShareSuccessModal from '../components/ShareSuccessModal';
import { getSubcategoryTranslation } from '../i18n-subcategories';

// Sortable Image Item Component
const SortableImageItem = ({ id, img, idx, onRemove, isMobile }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    touchAction: 'none', // Critical for mobile drag
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-square rounded-xl overflow-hidden border-2 ${
        isDragging ? 'border-primary shadow-xl scale-105 opacity-90' : 'border-border'
      } cursor-grab active:cursor-grabbing transition-all duration-200`}
      {...attributes}
      {...listeners}
    >
      <img src={img} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover pointer-events-none select-none" draggable={false} />
      {idx === 0 && (
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-white text-xs font-semibold rounded-lg pointer-events-none">
          Main
        </div>
      )}
      {/* Drag handle indicator - always visible on mobile */}
      <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-black/50 text-white text-xs rounded-lg transition-opacity pointer-events-none flex items-center gap-1 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <GripVertical className="w-3 h-3" />
        {isMobile ? 'Hold & drag' : 'Drag'}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onRemove(idx);
        }}
        className={`absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg transition-opacity hover:bg-red-600 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// Vehicle brands for cars and motorcycles
const CAR_BRANDS = [
  'Audi', 'BMW', 'Chevrolet', 'Daihatsu', 'Fiat', 'Ford', 'Honda', 'Hyundai',
  'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Mazda', 'Mercedes-Benz', 'Mitsubishi',
  'Nissan', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Seat', 'Skoda', 'Subaru',
  'Suzuki', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo', 'Other'
];

const MOTORCYCLE_BRANDS = [
  'BMW', 'Ducati', 'Harley-Davidson', 'Honda', 'Indian', 'Kawasaki', 'KTM',
  'Moto Guzzi', 'Royal Enfield', 'Suzuki', 'Triumph', 'Vespa', 'Yamaha', 'Other'
];

const BOAT_BRANDS = [
  'Bayliner', 'Boston Whaler', 'Brunswick', 'Chaparral', 'Cobalt', 'Formula',
  'Grady-White', 'Jeanneau', 'MasterCraft', 'Malibu', 'Nautic Star', 'Ranger',
  'Regal', 'Robalo', 'Sea Ray', 'Sunseeker', 'Tracker', 'Yamaha', 'Other'
];

const SERVICE_HISTORY_OPTIONS = [
  { value: 'full', label: 'Full Service History' },
  { value: 'partial', label: 'Partial Service History' },
  { value: 'none', label: 'No Service History' },
  { value: 'unknown', label: 'Unknown' }
];

const DAMAGE_HISTORY_OPTIONS = [
  { value: 'none', label: 'No Damage History' },
  { value: 'minor', label: 'Minor Damage (Repaired)' },
  { value: 'major', label: 'Major Damage (Repaired)' },
  { value: 'salvage', label: 'Salvage Title' }
];

const ITEM_CONDITIONS = [
  { value: 'new', label: 'New', description: 'Brand new, never used' },
  { value: 'second_hand', label: 'Second Hand', description: 'Used but good condition' },
  { value: 'fixer_upper', label: 'Fixer Upper', description: 'Needs repair' }
];

// Listing Type Cards Configuration - with translations
const LISTING_TYPES_CONFIG = {
  sale: { 
    icon: Tag, 
    color: 'from-green-500 to-emerald-600',
    label: { en: 'Sell', nl: 'Verkopen', es: 'Vender' },
    description: { en: 'Sell your item', nl: 'Verkoop je artikel', es: 'Vende tu artículo' },
    tip: { en: 'Best for items you want to sell immediately at a fixed price', nl: 'Ideaal voor artikelen die je direct wilt verkopen tegen een vaste prijs', es: 'Ideal para artículos que quieres vender inmediatamente a un precio fijo' }
  },
  rent: { 
    icon: Key, 
    color: 'from-blue-500 to-cyan-600',
    label: { en: 'Rent', nl: 'Verhuren', es: 'Alquilar' },
    description: { en: 'Rent out your property', nl: 'Verhuur je eigendom', es: 'Alquila tu propiedad' },
    tip: { en: 'Perfect for properties, vehicles, or equipment for temporary use', nl: 'Perfect voor woningen, voertuigen of apparatuur voor tijdelijk gebruik', es: 'Perfecto para propiedades, vehículos o equipos de uso temporal' }
  },
  trade: { 
    icon: Repeat, 
    color: 'from-purple-500 to-violet-600',
    label: { en: 'Trade', nl: 'Ruilen', es: 'Intercambiar' },
    description: { en: 'Trade for something', nl: 'Ruil voor iets anders', es: 'Intercambia por algo' },
    tip: { en: 'Exchange your item for something else without money', nl: 'Ruil je artikel voor iets anders zonder geld', es: 'Intercambia tu artículo por otra cosa sin dinero' }
  },
  service: { 
    icon: Briefcase, 
    color: 'from-orange-500 to-amber-600',
    label: { en: 'Service', nl: 'Dienst', es: 'Servicio' },
    description: { en: 'Offer your services', nl: 'Bied je diensten aan', es: 'Ofrece tus servicios' },
    tip: { en: 'Offer your skills like cleaning, repairs, or lessons', nl: 'Bied je vaardigheden aan zoals schoonmaken, reparaties of lessen', es: 'Ofrece tus habilidades como limpieza, reparaciones o clases' }
  },
  yard_sale: { 
    icon: ShoppingBag, 
    color: 'from-pink-500 to-rose-600',
    label: { en: 'Yard Sale', nl: 'Garage Sale', es: 'Venta de Garaje' },
    description: { en: 'Multiple items bundle', nl: 'Meerdere artikelen bundel', es: 'Paquete de múltiples artículos' },
    tip: { en: 'Sell multiple items at once, great for clearing out', nl: 'Verkoop meerdere artikelen tegelijk, ideaal voor opruimen', es: 'Vende múltiples artículos a la vez, ideal para despejar' }
  },
  auction: { 
    icon: Gavel, 
    color: 'from-red-500 to-rose-600',
    label: { en: 'Auction', nl: 'Veiling', es: 'Subasta' },
    description: { en: 'Let buyers bid', nl: 'Laat kopers bieden', es: 'Deja que los compradores oferten' },
    tip: { en: 'Set a starting price and let buyers compete with bids', nl: 'Stel een startprijs in en laat kopers concurreren met biedingen', es: 'Establece un precio inicial y deja que los compradores compitan con ofertas' }
  }
};

const LISTING_TYPE_ORDER = ['sale', 'rent', 'trade', 'service', 'yard_sale', 'auction'];

// Island Flags
const ISLAND_FLAGS = {
  aruba: { flag: '🇦🇼', name: 'Aruba' },
  curacao: { flag: '🇨🇼', name: 'Curaçao' },
  bonaire: { flag: '🇧🇶', name: 'Bonaire' },
};

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const { currency } = useCurrency();
  const { islands, currentIsland } = useIsland();
  const isEditing = Boolean(id);
  const isBusiness = user?.account_type === 'business';

  // ============== STEP STATE ==============
  const [currentStep, setCurrentStep] = useState(0); // 0=Type, 1=Advertentie, 2=Afronden
  const [visitedSteps, setVisitedSteps] = useState(new Set([0]));
  const [mobileStepDropdownOpen, setMobileStepDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Detect mobile for drag hints
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============== MAGIC IMPORT STATE ==============
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importsRemaining, setImportsRemaining] = useState(5);
  const [aiImportEnabled, setAiImportEnabled] = useState(true); // Feature flag from admin

  // ============== CORE STATES ==============
  // Start with loading=true when editing to prevent showing empty Type Selection step
  const [loading, setLoading] = useState(isEditing);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [selectedIslands, setSelectedIslands] = useState([]);
  const [locationFetchedViaGPS, setLocationFetchedViaGPS] = useState(false);
  
  const [allCategories, setAllCategories] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [singleCategoryMode, setSingleCategoryMode] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    listing_type: '',
    location: {
      address: '',
      city: '',
      area: '',
      country: '',
      latitude: null,
      longitude: null,
      display_type: 'area', // Default to area for privacy-first approach
    },
    pricing: {
      amount: '',
      currency: currency === 'both' ? 'USD' : currency,
      price_type: isBusiness ? 'incl_ob' : 'no_ob',
      ob_percentage: '',
      rental_period: 'monthly', // Default for rent: monthly. Options: hourly, daily, weekly, monthly, yearly
      additional_costs: '',
      additional_costs_description: '',
      bidding_allowed: false,
      minimum_bid: '',
    },
    trade: {
      open_for_trade: false,
      trade_value: '',
      trade_value_currency: currency === 'both' ? 'USD' : currency,
      trade_preferences: '',
      wanted_items: '',
      open_to_offers: false,
      willing_to_add_cash: false,
      cash_range: '',
    },
    yard_sale: {
      items: [],
      currency: currency === 'both' ? 'USD' : (currency || 'USD'), // Single currency for all items
      enable_bundle_deal: false, // Toggle for bundle pricing
      bundle_discount_type: 'percentage', // 'percentage' or 'amount'
      bundle_discount_value: '',
      allow_individual_purchase: true,
      enable_whatsapp_per_item: true,
    },
    auction: {
      starting_price: '',
      reserve_price: '',
      bid_increment: '1',
      auction_end_time: '',
      auto_extend: true,
    },
    images: [],
    video: null,
    video_thumbnail: null,
    youtube_url: '',
    videoInputType: 'upload', // 'upload' or 'youtube'
    features: [],
    contact_phone: user?.phone || '',
    contact_email: user?.email || '',
    contact_whatsapp: user?.whatsapp || '',
    enable_whatsapp: true,
    // Business CTA & Campaign fields
    cta_url: '',
    cta_type: '',
    campaign_start: '',
    campaign_end: '',
    property_amenities: {
      pool: false,
      sea_view: false,
      garden: false,
      garage: false,
      carport: false,
      air_conditioning: false,
      furnished: false,
      parking: false,
      security: false,
      balcony: false,
    },
  });

  const [newFeature, setNewFeature] = useState('');
  const [newYardSaleItem, setNewYardSaleItem] = useState({
    name: '',
    description: '',
    price: '',
    currency: currency || 'USD',
    quantity: 1,
    image: '',
    status: 'available',
  });
  
  const [yardSaleItemUploading, setYardSaleItemUploading] = useState(false);
  const [vinData, setVinData] = useState(null);
  const [vinLoading, setVinLoading] = useState(false);
  const [vinError, setVinError] = useState('');

  // Draft states
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const autoSaveTimerRef = useRef(null);
  const initialFormDataRef = useRef(null);

  // Share Success Modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [newListingData, setNewListingData] = useState(null);

  const [categoryProperties, setCategoryProperties] = useState([]);
  const [customProperties, setCustomProperties] = useState({});
  const [loadingProperties, setLoadingProperties] = useState(false);

  // ============== DRAG & DROP SENSORS ==============
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Generate unique IDs for sortable items - use actual URLs as IDs
  const imageIds = useMemo(() => formData.images, [formData.images]);

  // Handle drag end - reorder images
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.images.indexOf(active.id);
        const newIndex = prev.images.indexOf(over.id);
        
        if (oldIndex === -1 || newIndex === -1) return prev;
        
        const newImages = arrayMove(prev.images, oldIndex, newIndex);
        
        // Notify user if main image changed
        if (oldIndex === 0 || newIndex === 0) {
          toast.success('Main photo updated!');
        }
        
        return {
          ...prev,
          images: newImages
        };
      });
    }
  }, []);

  // ============== STEP VALIDATION (3-STEP FLOW) ==============
  const getStepStatus = useCallback((stepNum) => {
    // Step 0: Type Selection
    if (stepNum === 0) {
      return formData.listing_type ? 'complete' : 'pending';
    }
    
    // Step 1: Advertentie (Story + Data + Media combined)
    if (stepNum === 1) {
      const hasTitle = formData.title && formData.title.trim().length >= 3;
      const hasCategory = formData.category && formData.category.trim() !== '';
      const hasSubcategory = formData.subcategory && formData.subcategory.trim() !== '';
      const hasIsland = selectedIslands.length > 0;
      const hasDescription = formData.description && formData.description.trim().length >= 10;
      const hasImages = formData.images.length > 0;
      
      // Pricing depends on listing type and CTA
      let hasPricing = false;
      const hasCTAType = formData.cta_type && formData.cta_type !== '';
      if (formData.listing_type === 'yard_sale') {
        hasPricing = formData.yard_sale?.items?.length > 0;
      } else if (formData.listing_type === 'auction') {
        hasPricing = formData.auction?.starting_price && formData.auction?.auction_end_time;
      } else if (formData.listing_type === 'trade') {
        hasPricing = !!formData.trade?.wanted_items;
      } else if (formData.listing_type === 'giveaway') {
        hasPricing = true;
      } else {
        hasPricing = hasCTAType || (formData.pricing?.amount && parseFloat(formData.pricing.amount) > 0);
      }
      
      if (hasTitle && hasCategory && hasSubcategory && hasIsland && hasDescription && hasImages && hasPricing) {
        return 'complete';
      }
      return visitedSteps.has(1) ? 'incomplete' : 'pending';
    }
    
    // Step 2: Afronden (Contact & Location)
    if (stepNum === 2) {
      if (formData.contact_phone || formData.contact_email) return 'complete';
      return visitedSteps.has(2) ? 'incomplete' : 'pending';
    }
    
    return 'pending';
  }, [formData, selectedIslands, visitedSteps]);

  const canPublish = useMemo(() => {
    return [1, 2].every(step => getStepStatus(step) === 'complete');
  }, [getStepStatus]);

  // ============== EFFECTS ==============
  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchFeatureFlags();
    if (!isEditing) checkForDrafts();
  }, [language]); // Re-fetch categories when language changes

  useEffect(() => {
    // For editing, we need user to be loaded (indicates auth cookies are set)
    // Token check removed as we use httpOnly cookies for API auth
    if (id && user) {
      fetchListing();
    }
  }, [id, user]);

  useEffect(() => {
    if (formData.listing_type) {
      filterCategoriesByType(formData.listing_type);
    }
  }, [formData.listing_type, allCategories]);

  useEffect(() => {
    if (formData.category && formData.subcategory && formData.listing_type) {
      fetchCategoryProperties();
    }
  }, [formData.category, formData.subcategory, formData.listing_type]);

  // Auto-save draft
  useEffect(() => {
    if (!isEditing && hasUnsavedChanges && formData.title) {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(() => saveDraft(false), 30000);
    }
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, hasUnsavedChanges, isEditing]);

  // ============== API CALLS ==============
  const fetchCategories = async () => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      const res = await axios.get(`${API}/categories?lang=${language}&_t=${timestamp}`);
      setCategories(res.data);
      setAllCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setAllLocations(res.data);
      setLocations(res.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`);
      if (res.data.ai_screenshot_import_enabled !== undefined) {
        setAiImportEnabled(res.data.ai_screenshot_import_enabled);
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      // Default to enabled on error
    }
  };

  const fetchListing = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/listings/${id}`);
      const listing = res.data;
      
      // Use functional updater to avoid stale closure issues
      setFormData(prev => ({
        ...prev,
        // Spread all listing fields
        title: listing.title || '',
        description: listing.description || '',
        category: listing.category || '',
        subcategory: listing.subcategory || '',
        listing_type: listing.listing_type || '',
        images: listing.images || [],
        video: listing.video || null,
        video_thumbnail: listing.video_thumbnail || null,
        youtube_url: listing.youtube_url || '',
        videoInputType: listing.youtube_url ? 'youtube' : 'upload',
        features: listing.features || [],
        contact_phone: listing.contact_phone || '',
        contact_email: listing.contact_email || '',
        contact_whatsapp: listing.contact_whatsapp || '',
        enable_whatsapp: listing.enable_whatsapp !== false,
        // Nested objects with fallback to previous state
        location: listing.location || prev.location,
        pricing: listing.pricing || prev.pricing,
        trade: listing.trade || prev.trade,
        // Yard sale with backwards compatibility for enable_bundle_deal
        yard_sale: listing.yard_sale ? {
          ...listing.yard_sale,
          // If existing listing has bundle_price, enable the toggle automatically
          enable_bundle_deal: listing.yard_sale.bundle_price ? true : (listing.yard_sale.enable_bundle_deal || false),
        } : prev.yard_sale,
        // Map auction data - backend uses 'end_time', some legacy data uses 'auction_end_time'
        auction: listing.auction ? {
          starting_price: listing.auction.starting_price || '',
          reserve_price: listing.auction.reserve_price || '',
          bid_increment: listing.auction.bid_increment || '',
          // Handle both 'end_time' (new format) and 'auction_end_time' (legacy format)
          auction_end_time: (listing.auction.end_time || listing.auction.auction_end_time) 
            ? (listing.auction.end_time || listing.auction.auction_end_time).slice(0, 16) 
            : '', // Format for datetime-local input
          auto_extend: listing.auction.auto_extend !== false,
        } : prev.auction,
        vehicle: listing.vehicle || prev.vehicle,
        boat: listing.boat || prev.boat,
        item_condition: listing.item_condition || prev.item_condition,
        property_details: listing.property_details || prev.property_details,
        property_amenities: listing.property_amenities || prev.property_amenities,
      }));
      
      // Load saved custom properties for editing
      if (listing.custom_properties) {
        setCustomProperties(listing.custom_properties);
      }
      
      if (listing.visible_islands) {
        setSelectedIslands(listing.visible_islands);
      }
      
      // When editing, start at Step 1 since type is already selected
      if (listing.listing_type) {
        setCurrentStep(1);
        setVisitedSteps(new Set([0, 1, 2, 3, 4]));
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
      toast.error('Failed to load listing');
      navigate('/my-listings');
    } finally {
      setLoading(false);
    }
  };

  const filterCategoriesByType = (listingType) => {
    // Most listing types should allow most categories
    // Only restrict categories that truly don't make sense for a type
    const typeCategories = {
      sale: ['vehicles', 'real-estate', 'electronics', 'furniture', 'fashion', 'outdoor', 'watersport', 'pets', 'kid&baby', 'business&industrial', 'health&beauty', 'tickets', 'security', 'other'],
      rent: ['real-estate', 'vehicles', 'car-rental', 'outdoor', 'watersport', 'electronics', 'furniture', 'business&industrial', 'other'],
      trade: ['vehicles', 'electronics', 'furniture', 'fashion', 'outdoor', 'watersport', 'pets', 'kid&baby', 'business&industrial', 'health&beauty', 'other'],
      service: ['services', 'taxis', 'tours', 'security', 'jobs', 'health&beauty', 'other'],
      yard_sale: ['furniture', 'electronics', 'fashion', 'outdoor', 'kid&baby', 'health&beauty', 'pets', 'other'],
      auction: ['vehicles', 'real-estate', 'electronics', 'furniture', 'fashion', 'outdoor', 'watersport', 'pets', 'business&industrial', 'other'],
    };
    
    const allowed = typeCategories[listingType] || [];
    const filtered = allCategories.filter(c => allowed.includes(c.id));
    setAllowedCategories(filtered);
    
    if (filtered.length === 1) {
      setSingleCategoryMode(true);
      setFormData(prev => ({ ...prev, category: filtered[0].id }));
    } else {
      setSingleCategoryMode(false);
    }
  };

  const fetchCategoryProperties = async () => {
    setLoadingProperties(true);
    try {
      const res = await axios.get(`${API}/categories/${formData.category}/properties`, {
        params: { 
          subcategory: formData.subcategory,
          listing_type: formData.listing_type 
        }
      });
      setCategoryProperties(res.data.properties || []);
    } catch (error) {
      setCategoryProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  // ============== MAGIC IMPORT FUNCTION ==============
  // ============== MAGIC IMPORT FUNCTIONS ==============
  const handleTextImport = async () => {
    const text = importText.trim();
    
    if (!text) {
      toast.error(language === 'nl' ? 'Plak eerst de advertentietekst' : 'Please paste the ad text first');
      return;
    }
    
    if (text.length < 10) {
      toast.error(language === 'nl' ? 'Tekst is te kort' : 'Text is too short');
      return;
    }

    setImporting(true);
    setImportSuccess(false);

    try {
      const response = await axios.post(`${API}/import/text`, {
        text: text,
        user_id: user?.id || 'anonymous'
      });

      const data = response.data;

      if (data.success) {
        // Auto-fill form fields
        setFormData(prev => ({
          ...prev,
          title: data.title || prev.title,
          description: data.description || prev.description,
          category: data.category || prev.category,
          subcategory: data.subcategory || prev.subcategory,
          condition: data.condition || prev.condition,
          pricing: {
            ...prev.pricing,
            amount: data.price ?? prev.pricing?.amount,
            currency: data.currency || prev.pricing?.currency || currency || 'USD',
          },
        }));
        
        // Fill in attributes if available
        if (data.attributes && typeof data.attributes === 'object') {
          setCustomProperties(prev => ({
            ...prev,
            ...data.attributes
          }));
        }

        setImportSuccess(true);
        setImportsRemaining(data.imports_remaining ?? importsRemaining - 1);
        toast.success(data.message || (language === 'nl' ? 'Gegevens geïmporteerd!' : 'Data imported!'));
        
        // Clear the text after success
        setTimeout(() => {
          setImportText('');
        }, 2000);
      } else {
        toast.error(data.message || (language === 'nl' ? 'Kon gegevens niet ophalen' : 'Could not extract data'));
        if (data.imports_remaining !== undefined) {
          setImportsRemaining(data.imports_remaining);
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || 
        (language === 'nl' ? 'Er ging iets mis bij het importeren' : 'Something went wrong while importing');
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const clearImportText = () => {
    setImportText('');
    setImportSuccess(false);
  };

  // ============== DRAFT FUNCTIONS ==============
  const saveDraft = useCallback(async (showToast = true) => {
    if (!user || isEditing) return;
    
    setSavingDraft(true);
    try {
      const draftData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        listing_type: formData.listing_type,
        images: formData.images,
        pricing: formData.pricing,
        location: formData.location,
      };
      
      if (draftId) {
        await axios.put(`${API}/drafts/${draftId}`, draftData);
      } else {
        const res = await axios.post(`${API}/drafts`, draftData);
        setDraftId(res.data.id);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      if (showToast) toast.success('Draft saved');
    } catch (error) {
      if (showToast) toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  }, [user, isEditing, formData, draftId]);

  const checkForDrafts = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/drafts`);
      if (res.data.length > 0) {
        setAvailableDrafts(res.data);
        setShowDraftDialog(true);
      }
    } catch (error) {
      console.error('Error checking drafts:', error);
    }
  };

  const loadDraft = (draft) => {
    setFormData(prev => ({
      ...prev,
      ...draft,
      pricing: draft.pricing || prev.pricing,
      location: draft.location || prev.location,
    }));
    setDraftId(draft.id);
    setShowDraftDialog(false);
    
    if (draft.listing_type) {
      setCurrentStep(1);
      setVisitedSteps(new Set([0, 1]));
    }
    
    toast.success('Draft loaded');
  };

  const deleteDraft = async () => {
    if (!draftId || !user) return;
    try {
      await axios.delete(`${API}/drafts/${draftId}`);
      setDraftId(null);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const deleteDraftById = async (id) => {
    try {
      await axios.delete(`${API}/drafts/${id}`);
      setAvailableDrafts(prev => prev.filter(d => d.id !== id));
      toast.success('Draft deleted');
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const startFresh = () => {
    setShowDraftDialog(false);
  };

  // ============== HANDLERS ==============
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHasUnsavedChanges(true);
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: type === 'checkbox' ? checked : value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleTypeSelect = (typeId) => {
    setFormData(prev => ({ ...prev, listing_type: typeId, category: '', subcategory: '' }));
    setHasUnsavedChanges(true);
    // Auto-advance to Step 1
    setCurrentStep(1);
    setVisitedSteps(prev => new Set([...prev, 1]));
  };

  // Single island selection - user can only pick one location
  const handleIslandSelect = (islandId) => {
    // If clicking the same island, deselect it; otherwise select the new one
    setSelectedIslands(prev => {
      if (prev.includes(islandId)) {
        return []; // Deselect
      }
      return [islandId]; // Select only this island
    });
    setHasUnsavedChanges(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const uploaded = [];
    
    for (const file of files) {
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        const res = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        });
        if (res.data?.url) {
          uploaded.push(res.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    if (uploaded.length > 0) {
      setFormData(prev => ({ ...prev, images: [...prev.images, ...uploaded] }));
      setHasUnsavedChanges(true);
      toast.success(`${uploaded.length} image(s) uploaded`);
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setHasUnsavedChanges(true);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB');
      return;
    }
    
    setUploadingVideo(true);
    setUploadProgress(0);
    setUploadMessage('Uploading video...');
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const res = await axios.post(`${API}/upload/video`, uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      if (res.data?.url) {
        setFormData(prev => ({ 
          ...prev, 
          video: res.data.url,
          video_thumbnail: res.data.thumbnail || null
        }));
        setHasUnsavedChanges(true);
        toast.success('Video uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      setUploadMessage('');
    }
  };

  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
      setHasUnsavedChanges(true);
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
    setHasUnsavedChanges(true);
  };

  // Custom Property Handler (for dynamic admin-defined properties)
  const handleCustomPropertyChange = (key, value) => {
    setCustomProperties(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Yard Sale Item Functions
  const addYardSaleItem = () => {
    if (!newYardSaleItem.name || !newYardSaleItem.price) {
      toast.error('Item name and price are required');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      yard_sale: {
        ...prev.yard_sale,
        items: [...prev.yard_sale.items, {
          name: newYardSaleItem.name,
          description: newYardSaleItem.description,
          price: parseFloat(newYardSaleItem.price),
          quantity: newYardSaleItem.quantity || 1,
          image: newYardSaleItem.image || '',
          status: 'available',
          // Currency is inherited from yard_sale.currency, not per item
        }]
      }
    }));
    
    setNewYardSaleItem({
      name: '',
      description: '',
      price: '',
      quantity: 1,
      image: '',
      status: 'available',
    });
    setHasUnsavedChanges(true);
  };

  const removeYardSaleItem = (index) => {
    setFormData(prev => ({
      ...prev,
      yard_sale: {
        ...prev.yard_sale,
        items: prev.yard_sale.items.filter((_, i) => i !== index)
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Edit yard sale item - load into form for editing
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  
  const editYardSaleItem = (index) => {
    const item = formData.yard_sale?.items?.[index];
    if (item) {
      setNewYardSaleItem({
        name: item.name || '',
        description: item.description || '',
        price: item.price?.toString() || '',
        quantity: item.quantity || 1,
        image: item.image || '',
        status: item.status || 'available',
      });
      setEditingItemIndex(index);
    }
  };

  const saveEditedYardSaleItem = () => {
    if (!newYardSaleItem.name || !newYardSaleItem.price) {
      toast.error('Item name and price are required');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      yard_sale: {
        ...prev.yard_sale,
        items: prev.yard_sale.items.map((item, idx) => 
          idx === editingItemIndex ? {
            ...item,
            name: newYardSaleItem.name,
            description: newYardSaleItem.description,
            price: parseFloat(newYardSaleItem.price),
            quantity: newYardSaleItem.quantity || 1,
            image: newYardSaleItem.image || '',
          } : item
        )
      }
    }));
    
    // Reset form
    setNewYardSaleItem({
      name: '',
      description: '',
      price: '',
      quantity: 1,
      image: '',
      status: 'available',
    });
    setEditingItemIndex(null);
    setHasUnsavedChanges(true);
    toast.success(language === 'nl' ? 'Artikel bijgewerkt' : 'Item updated');
  };

  const cancelEditItem = () => {
    setNewYardSaleItem({
      name: '',
      description: '',
      price: '',
      quantity: 1,
      image: '',
      status: 'available',
    });
    setEditingItemIndex(null);
  };

  const handleYardSaleItemImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setYardSaleItemUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const res = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data?.url) {
        setNewYardSaleItem(prev => ({ ...prev, image: res.data.url }));
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setYardSaleItemUploading(false);
    }
  };

  // Get current location
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported');
      return;
    }
    
    setGettingLocation(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      
      const { latitude, longitude } = position.coords;
      
      // Reverse geocode
      const res = await axios.get(`${API}/geocode/reverse`, {
        params: { lat: latitude, lng: longitude }
      });
      
      if (res.data) {
        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            address: res.data.address || '',
            city: res.data.city || '',
            area: res.data.area || '',
            country: res.data.country || '',
            latitude,
            longitude,
          }
        }));
        setLocationFetchedViaGPS(true);
        setHasUnsavedChanges(true);
        toast.success('Location found');
      }
    } catch (error) {
      toast.error('Failed to get location');
    } finally {
      setGettingLocation(false);
    }
  };

  // ============== SUBMIT ==============
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps
    const errors = [];
    
    if (!formData.listing_type) errors.push('Please select a listing type');
    if (!formData.title || formData.title.trim().length < 3) errors.push('Title must be at least 3 characters');
    if (!formData.category) errors.push('Please select a category');
    if (!formData.subcategory) errors.push('Please select a subcategory');
    if (selectedIslands.length === 0) errors.push('Please select an island location');
    if (!formData.description || formData.description.trim().length < 10) errors.push('Description must be at least 10 characters');
    
    // Type-specific validation
    if (formData.listing_type === 'yard_sale') {
      if (!formData.yard_sale?.items?.length) errors.push('Add at least one item to your yard sale');
    } else if (formData.listing_type === 'auction') {
      if (!formData.auction?.starting_price) errors.push('Starting price is required');
      if (!formData.auction?.auction_end_time) errors.push('Auction end date is required');
    } else if (formData.listing_type !== 'trade') {
      // For sale/rent/service: price is required UNLESS a CTA type is set (business promotions)
      const hasCTAType = formData.cta_type && formData.cta_type !== '';
      if (!hasCTAType && (!formData.pricing?.amount || parseFloat(formData.pricing.amount) <= 0)) {
        errors.push('Please enter a valid price');
      }
      // If CTA type is set, URL is required
      if (hasCTAType && !formData.cta_url) {
        errors.push(language === 'nl' ? 'Vul een externe URL in voor je actie' : 'Please enter an external URL for your promotion');
      }
    }
    
    if (formData.images.length === 0) errors.push('Please add at least one image');
    
    if (errors.length > 0) {
      errors.forEach((err, idx) => {
        setTimeout(() => toast.error(err), idx * 300);
      });
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        listing_type: formData.listing_type,
        location: formData.location,
        visible_islands: selectedIslands,
        images: formData.images,
        video: formData.video,
        video_thumbnail: formData.video_thumbnail,
        youtube_url: formData.youtube_url || null,
        features: formData.features,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        pricing: {
          amount: parseFloat(formData.pricing?.amount) || 0,
          currency: formData.pricing?.currency || currency || 'USD',
          price_type: formData.pricing?.price_type || 'no_ob',
          ob_percentage: formData.pricing?.ob_percentage ? parseFloat(formData.pricing.ob_percentage) : null,
          rental_period: formData.listing_type === 'rent' ? (formData.pricing?.rental_period || 'monthly') : null,
          bidding_allowed: formData.pricing?.bidding_allowed || false,
          minimum_bid: formData.pricing?.bidding_allowed && formData.pricing?.minimum_bid ? parseFloat(formData.pricing.minimum_bid) : null,
          price_unit: formData.pricing?.price_unit || null,
        },
      };

      if (formData.listing_type === 'trade') {
        payload.trade = {
          wanted_items: formData.trade?.wanted_items || '',
          trade_value: formData.trade?.trade_value ? parseFloat(formData.trade.trade_value) : null,
          open_to_offers: formData.trade?.open_to_offers || false,
          willing_to_add_cash: formData.trade?.willing_to_add_cash || false,
        };
      }

      if (formData.listing_type === 'yard_sale') {
        // Calculate bundle price based on discount type and value (including quantities)
        const totalPrice = formData.yard_sale?.items?.reduce((sum, item) => 
          sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0
        ) || 0;
        const discountType = formData.yard_sale?.bundle_discount_type || 'percentage';
        const discountValue = parseFloat(formData.yard_sale?.bundle_discount_value) || 0;
        
        let bundlePrice = null;
        if (formData.yard_sale?.enable_bundle_deal && discountValue > 0) {
          if (discountType === 'percentage') {
            bundlePrice = totalPrice * (1 - discountValue / 100);
          } else {
            bundlePrice = totalPrice - discountValue;
          }
        }
        
        payload.yard_sale = {
          items: formData.yard_sale?.items || [],
          currency: formData.yard_sale?.currency || 'USD',
          enable_bundle_deal: formData.yard_sale?.enable_bundle_deal || false,
          bundle_discount_type: formData.yard_sale?.enable_bundle_deal ? discountType : null,
          bundle_discount_value: formData.yard_sale?.enable_bundle_deal ? discountValue : null,
          bundle_price: bundlePrice,
          allow_individual_purchase: formData.yard_sale?.allow_individual_purchase !== false,
          enable_whatsapp_per_item: formData.yard_sale?.enable_whatsapp_per_item !== false,
        };
      }

      if (formData.listing_type === 'auction') {
        payload.auction = {
          starting_price: parseFloat(formData.auction?.starting_price) || 0,
          reserve_price: formData.auction?.reserve_price ? parseFloat(formData.auction.reserve_price) : null,
          bid_increment: parseFloat(formData.auction?.bid_increment) || 1,
          end_time: formData.auction?.auction_end_time ? new Date(formData.auction.auction_end_time).toISOString() : null,
          auto_extend: formData.auction?.auto_extend !== false,
        };
        payload.pricing.amount = parseFloat(formData.auction?.starting_price) || 0;
      }

      if (formData.category === 'vehicles' || formData.category === 'car-rental') {
        payload.vehicle = {
          brand: formData.vehicle?.brand || null,
          model: formData.vehicle?.model || null,
          year: formData.vehicle?.year ? parseInt(formData.vehicle.year) : null,
          mileage: formData.vehicle?.mileage ? parseInt(formData.vehicle.mileage) : null,
          fuel_type: formData.vehicle?.fuel_type || null,
          transmission: formData.vehicle?.transmission || null,
          color: formData.vehicle?.color || null,
        };
      }

      if (['Boats', 'Jet Skis', 'Yachts', 'Watercraft'].includes(formData.subcategory)) {
        payload.boat = {
          brand: formData.boat?.brand || null,
          model: formData.boat?.model || null,
          year: formData.boat?.year ? parseInt(formData.boat.year) : null,
          engine_hours: formData.boat?.engine_hours ? parseInt(formData.boat.engine_hours) : null,
          engine_type: formData.boat?.engine_type || null,
          fuel_type: formData.boat?.fuel_type || null,
          length_ft: formData.boat?.length_ft ? parseFloat(formData.boat.length_ft) : null,
        };
      }

      if (['furniture', 'electronics', 'fashion', 'other'].includes(formData.category) && formData.item_condition?.condition) {
        payload.item_condition = {
          condition: formData.item_condition.condition,
          brand: formData.item_condition?.brand || null,
        };
      }

      if (formData.category === 'real-estate' && formData.property_details?.property_type) {
        payload.property_details = formData.property_details;
      }

      if (Object.keys(customProperties).length > 0) {
        payload.custom_properties = customProperties;
      }

      // Add CTA & Campaign fields for verified business accounts
      if (user?.account_type === 'business' && user?.is_verified_business) {
        if (formData.cta_url) {
          // Normalize URL - ensure it has https:// protocol
          let normalizedUrl = formData.cta_url.trim();
          if (normalizedUrl && !normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
            normalizedUrl = `https://${normalizedUrl}`;
          }
          payload.cta_url = normalizedUrl;
          payload.cta_type = formData.cta_type || 'promotion';
        }
        if (formData.campaign_start) {
          payload.campaign_start = new Date(formData.campaign_start).toISOString();
        }
        if (formData.campaign_end) {
          payload.campaign_end = new Date(formData.campaign_end).toISOString();
        }
      }

      if (isEditing) {
        await axios.put(`${API}/listings/${id}`, payload);
        toast.success('Listing updated successfully!');
        setHasUnsavedChanges(false);
        navigate('/my-listings');
      } else {
        // Create new listing and get the response with listing ID/slug
        const response = await axios.post(`${API}/listings`, payload);
        const newListing = response.data;
        await deleteDraft();
        setHasUnsavedChanges(false);
        
        // Show Share Success Modal instead of navigating immediately
        setNewListingData({
          id: newListing.id,
          slug: newListing.slug,
          title: formData.title
        });
        setShowShareModal(true);
      }
    } catch (error) {
      console.error('Listing error:', error.response?.data || error.message);
      // Handle Pydantic validation errors (array of objects with msg field)
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Extract error messages from Pydantic validation errors
        const errorMessages = errorDetail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        toast.error(errorMessages || 'Validation error');
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to create listing');
      }
    } finally {
      setLoading(false);
    }
  };

  // ============== NAVIGATION ==============
  const goToStep = (step) => {
    if (step === 0 || formData.listing_type) {
      setCurrentStep(step);
      setVisitedSteps(prev => new Set([...prev, step]));
    }
  };

  const nextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(prev => prev + 1);
      setVisitedSteps(prev => new Set([...prev, currentStep + 1]));
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Leave dialog handlers
  const saveAndLeave = async () => {
    await saveDraft(true);
    if (pendingNavigation === -1) {
      navigate(-1);
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowLeaveDialog(false);
  };

  const confirmLeave = () => {
    setHasUnsavedChanges(false);
    if (pendingNavigation === -1) {
      navigate(-1);
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
    }
    setShowLeaveDialog(false);
  };

  // ============== STEP CONTENT COMPONENTS ==============
  const selectedCategory = categories.find(c => c.id === formData.category);

  // Status Icon Component
  const StepStatusIcon = ({ status, stepNum }) => {
    if (status === 'complete') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    if (status === 'incomplete') {
      return <AlertCircle className="w-5 h-5 text-amber-500" />;
    }
    return <Circle className="w-5 h-5 text-muted-foreground/50" />;
  };

  const steps = [
    { num: 0, label: language === 'nl' ? 'Type' : 'Type', shortLabel: 'Type' },
    { num: 1, label: language === 'nl' ? 'Advertentie' : 'Listing', shortLabel: language === 'nl' ? 'Details' : 'Details' },
    { num: 2, label: language === 'nl' ? 'Afronden' : 'Finalize', shortLabel: language === 'nl' ? 'Klaar' : 'Done' },
  ];

  // ============== RENDER ==============
  return (
    <div className="min-h-screen bg-background" data-testid="create-listing-page">
      <Navbar />

      {/* Leave Confirmation Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-primary" />
              {language === 'nl' ? 'Voortgang opslaan?' : 'Save your progress?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'nl' 
                ? 'Je hebt niet-opgeslagen wijzigingen. Wil je deze advertentie als concept opslaan?'
                : 'You have unsaved changes. Would you like to save this listing as a draft?'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <button onClick={saveAndLeave} disabled={savingDraft} className="w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
              {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {language === 'nl' ? 'Opslaan als concept & Verlaten' : 'Save as Draft & Leave'}
            </button>
            <button onClick={confirmLeave} className="w-full px-4 py-3 bg-secondary text-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors">
              {language === 'nl' ? 'Verlaten zonder opslaan' : 'Leave Without Saving'}
            </button>
            <button onClick={() => setShowLeaveDialog(false)} className="w-full px-4 py-3 text-muted-foreground hover:text-foreground transition-colors">
              {language === 'nl' ? 'Annuleren' : 'Cancel'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Draft Selection Dialog */}
      <Dialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {language === 'nl' ? 'Concept voortzetten?' : 'Continue a draft?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'nl' 
                ? `Je hebt ${availableDrafts.length} opgeslagen concept${availableDrafts.length > 1 ? 'en' : ''}.`
                : `You have ${availableDrafts.length} saved draft${availableDrafts.length > 1 ? 's' : ''}.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
            {availableDrafts.map((draft) => (
              <div key={draft.id} className="flex items-center gap-3 p-3 bg-secondary/50 rounded-xl border border-border hover:border-primary/50 transition-colors">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center overflow-hidden">
                  {draft.images?.[0] ? (
                    <img src={draft.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate text-sm">{draft.title || (language === 'nl' ? 'Naamloos concept' : 'Untitled Draft')}</h4>
                  <p className="text-xs text-muted-foreground">{draft.category || (language === 'nl' ? 'Geen categorie' : 'No category')} • {new Date(draft.updated_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => loadDraft(draft)} className="px-3 py-1.5 bg-primary text-white text-sm rounded-xl hover:bg-primary/90 transition-colors">
                    {language === 'nl' ? 'Verdergaan' : 'Continue'}
                  </button>
                  <button onClick={() => deleteDraftById(draft.id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t border-border">
            <button onClick={startFresh} className="w-full px-4 py-3 bg-secondary text-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" />
              {language === 'nl' ? 'Nieuwe advertentie starten' : 'Start New Listing'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading Overlay when fetching listing for edit */}
      {isEditing && loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[150] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading listing...</p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 overflow-visible">
        {/* Mobile Sticky Header - Progress Bar */}
        <div className="lg:hidden sticky top-0 z-[100] bg-background/95 backdrop-blur-sm border-b border-border -mx-4 px-4 py-3 mb-6 safe-area-top">
          <div className="flex items-center justify-between mb-2">
            {/* Back/Close Button - Goes to previous step, or shows X at step 0 */}
            <button
              onClick={() => {
                if (currentStep > 0) {
                  // Go to previous step
                  setCurrentStep(currentStep - 1);
                } else {
                  // At step 0, exit the flow
                  if (hasUnsavedChanges && !isEditing) {
                    setPendingNavigation(-1);
                    setShowLeaveDialog(true);
                  } else {
                    navigate(-1);
                  }
                }
              }}
              className="flex items-center text-muted-foreground hover:text-primary transition-colors p-1"
              data-testid="mobile-back-button"
            >
              {currentStep === 0 ? (
                <X className="w-5 h-5" />
              ) : (
                <ArrowLeft className="w-5 h-5" />
              )}
            </button>
            
            {/* Step Dropdown */}
            <div className="relative">
              <button
                {...safeClick(() => setMobileStepDropdownOpen(!mobileStepDropdownOpen))}
                className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-xl text-sm font-medium touch-manipulation"
                data-testid="mobile-step-dropdown"
              >
                {language === 'nl' ? 'Stap' : 'Step'} {currentStep + 1} {language === 'nl' ? 'van' : 'of'} 3
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileStepDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileStepDropdownOpen && (
                <>
                  {/* Click outside overlay to close dropdown */}
                  <div 
                    className="fixed inset-0 z-[9998]" 
                    onClick={() => setMobileStepDropdownOpen(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-48 bg-card rounded-xl border border-border shadow-lg z-[9999]">
                    {steps.map((step) => (
                      <button
                        key={step.num}
                        onClick={() => {
                          goToStep(step.num);
                          setMobileStepDropdownOpen(false);
                        }}
                        disabled={step.num > 0 && !formData.listing_type}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-secondary/50 first:rounded-t-xl last:rounded-b-xl transition-colors ${
                          currentStep === step.num ? 'bg-primary/10 text-primary' : ''
                        } ${step.num > 0 && !formData.listing_type ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <StepStatusIcon status={getStepStatus(step.num)} stepNum={step.num} />
                        {step.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Draft Save */}
            {!isEditing && hasUnsavedChanges && (
              <button onClick={() => saveDraft(true)} disabled={savingDraft} className="p-2 text-primary">
                {savingDraft ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              </button>
            )}
          </div>
          
          {/* Progress Bar - with ring around current step */}
          <div className="flex gap-1.5">
            {[0, 1, 2].map((step) => {
              const status = getStepStatus(step);
              const isCurrentStep = step === currentStep;
              return (
                <div
                  key={step}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    status === 'complete' ? 'bg-green-500' :
                    status === 'incomplete' ? 'bg-amber-500' :
                    step <= currentStep ? 'bg-primary' : 'bg-muted'
                  } ${isCurrentStep ? 'ring-2 ring-offset-1 ring-offset-background ring-foreground/50' : ''}`}
                />
              );
            })}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar - Progress */}
          <div className="hidden lg:block w-64 flex-shrink-0 relative z-0">
            <div className="sticky top-24">
              <button
                onClick={() => {
                  if (hasUnsavedChanges && !isEditing) {
                    setPendingNavigation(-1);
                    setShowLeaveDialog(true);
                  } else {
                    navigate(-1);
                  }
                }}
                className="flex items-center text-muted-foreground hover:text-primary transition-colors mb-6"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {language === 'nl' ? 'Terug' : 'Back'}
              </button>

              <h1 className="text-xl font-bold text-foreground mb-6">
                {isEditing 
                  ? (language === 'nl' ? 'Advertentie Bewerken' : 'Edit Listing')
                  : (language === 'nl' ? 'Advertentie Plaatsen' : 'Create Listing')}
              </h1>
              
              <div className="space-y-1">
                {steps.map((step, idx) => {
                  const status = getStepStatus(step.num);
                  const isActive = currentStep === step.num;
                  const isDisabled = step.num > 0 && !formData.listing_type;
                  
                  return (
                    <button
                      key={step.num}
                      onClick={() => goToStep(step.num)}
                      disabled={isDisabled}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        isActive ? 'bg-primary text-white shadow-lg shadow-primary/20' :
                        isDisabled ? 'opacity-40 cursor-not-allowed' :
                        'hover:bg-secondary'
                      }`}
                      data-testid={`step-${step.num}-btn`}
                    >
                      {isActive ? (
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                          {step.num + 1}
                        </div>
                      ) : (
                        <StepStatusIcon status={status} stepNum={step.num} />
                      )}
                      <span className={`font-medium text-sm ${isActive ? 'text-white' : 'text-foreground'}`}>
                        {step.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Draft Status */}
              {!isEditing && (
                <div className="mt-6 pt-6 border-t border-border">
                  {lastSaved && (
                    <p className="text-xs text-muted-foreground mb-2">
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </p>
                  )}
                  {hasUnsavedChanges && (
                    <button onClick={() => saveDraft(true)} disabled={savingDraft} className="w-full px-4 py-2.5 bg-primary/10 text-primary rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary/20 transition-colors">
                      {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {language === 'nl' ? 'Concept Opslaan' : 'Save Draft'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div 
            className="flex-1 w-full lg:max-w-3xl relative z-10"
            style={{ 
              overflow: 'visible'
            }}
          >
            <form 
              onSubmit={handleSubmit} 
              style={{ overflow: 'visible' }}
            >
              
              {/* ============== STEP 0: TYPE SELECTION ============== */}
              {currentStep === 0 && (
                <div className="overflow-visible" data-testid="step-0-type">
                  <div className="animate-in fade-in duration-300">
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      {language === 'nl' ? 'Wat wil je doen?' : language === 'es' ? '¿Qué te gustaría hacer?' : 'What would you like to do?'}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      {language === 'nl' ? 'Kies het type advertentie dat het beste bij je past' : language === 'es' ? 'Elige el tipo de anuncio que mejor se adapte a ti' : 'Choose the listing type that best fits your needs'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {LISTING_TYPE_ORDER.map((typeId) => {
                      const type = LISTING_TYPES_CONFIG[typeId];
                      const Icon = type.icon;
                      const isSelected = formData.listing_type === typeId;
                      
                      return (
                        <button
                          key={typeId}
                          type="button"
                          onClick={() => handleTypeSelect(typeId)}
                          className={`group relative p-4 sm:p-6 rounded-2xl border-2 transition-all text-left ${
                            isSelected 
                              ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10' 
                              : 'border-border hover:border-primary/50 hover:shadow-md'
                          }`}
                          data-testid={`type-${typeId}`}
                        >
                          <div className="flex sm:flex-col items-start gap-3 sm:gap-0">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center sm:mb-4 transition-transform group-hover:scale-110 flex-shrink-0`}>
                              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-foreground mb-1 sm:mb-2">{type.label[language] || type.label.en}</h3>
                              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{type.tip[language] || type.tip.en}</p>
                            </div>
                          </div>
                          
                          {isSelected && (
                            <div className="absolute top-3 right-3">
                              <CheckCircle className="w-5 h-5 text-primary" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ============== STEP 1: THE STORY ============== */}
              {currentStep === 1 && (
                <div className="space-y-6" data-testid="step-1-story" style={{ overflow: 'visible' }}>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      {language === 'nl' ? 'Maak je advertentie' : 'Create Your Listing'}
                    </h2>
                    <p className="text-muted-foreground">
                      {language === 'nl' ? "Voeg foto's, beschrijving en prijs toe" : 'Add photos, description and price'}
                    </p>
                  </div>

                  {/* === SECTION: MEDIA (Photos & Video) === */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Image className="w-5 h-5 text-primary" />
                      {language === 'nl' ? "Foto's & Video" : 'Photos & Video'}
                      <span className="text-xs text-muted-foreground font-normal ml-auto">{formData.images.length}/10</span>
                    </h3>
                    
                    {/* Image Grid with Drag & Drop */}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={imageIds} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-4">
                          {formData.images.map((img, idx) => (
                            <SortableImageItem
                              key={img}
                              id={img}
                              img={img}
                              idx={idx}
                              onRemove={removeImage}
                              isMobile={isMobile}
                            />
                          ))}
                          
                          {/* Upload Button */}
                          <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer flex flex-col items-center justify-center transition-colors">
                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
                            {uploading ? (
                              <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-8 h-8 text-muted-foreground mb-1" />
                                <span className="text-xs text-muted-foreground">
                                  {language === 'nl' ? 'Toevoegen' : 'Add'}
                                </span>
                              </>
                            )}
                          </label>
                        </div>
                      </SortableContext>
                    </DndContext>
                    
                    <p className="text-xs text-muted-foreground">
                      {language === 'nl' ? 'Eerste foto wordt de hoofdfoto. Sleep om te herschikken.' : 'First image will be the main photo. Drag to reorder.'}
                    </p>
                    {formData.images.length > 0 && (
                      <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                        <Check className="w-3 h-3" /> {formData.images.length} {language === 'nl' ? "foto('s) geüpload" : 'image(s) uploaded'}
                      </p>
                    )}
                  </div>

                  {/* Video Upload or YouTube URL */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Video className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Video (Optioneel)' : 'Video (Optional)'}
                    </h3>
                    
                    {/* Video Type Toggle */}
                    {!formData.video && !formData.youtube_url && (
                      <div className="flex gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, videoInputType: 'upload' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            (!formData.videoInputType || formData.videoInputType === 'upload')
                              ? 'bg-primary text-white'
                              : 'bg-secondary text-foreground hover:bg-secondary/80'
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          {language === 'nl' ? 'Upload' : 'Upload'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, videoInputType: 'youtube' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            formData.videoInputType === 'youtube'
                              ? 'bg-red-500 text-white'
                              : 'bg-secondary text-foreground hover:bg-secondary/80'
                          }`}
                        >
                          <Play className="w-4 h-4" />
                          YouTube
                        </button>
                      </div>
                    )}
                    
                    {/* Uploaded Video Preview */}
                    {formData.video ? (
                      <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
                        <video 
                          src={formData.video} 
                          controls 
                          playsInline
                          preload="metadata"
                          className="w-full h-full object-contain" 
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, video: null, video_thumbnail: null }))}
                          className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : formData.youtube_url ? (
                      /* YouTube URL Preview */
                      <div className="relative">
                        <div className="aspect-video rounded-xl overflow-hidden bg-black flex items-center justify-center">
                          <img
                            src={`https://img.youtube.com/vi/${formData.youtube_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}/mqdefault.jpg`}
                            alt="YouTube thumbnail"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                              <Play className="w-8 h-8 text-white ml-1" fill="white" />
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, youtube_url: '', videoInputType: 'upload' }))}
                          className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : formData.videoInputType === 'youtube' ? (
                      /* YouTube URL Input */
                      <div className="space-y-3">
                        <div className="relative">
                          <input
                            type="url"
                            value={formData.youtube_url || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, youtube_url: e.target.value }))}
                            placeholder="https://youtube.com/watch?v=... of https://youtu.be/..."
                            className="w-full px-4 py-3 pl-10 rounded-xl border border-input bg-card text-foreground text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                            data-testid="youtube-url-input"
                          />
                          <Play className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {language === 'nl' 
                            ? 'Plak een YouTube link. De video wordt getoond op de advertentie pagina.'
                            : 'Paste a YouTube link. The video will be shown on the listing page.'}
                        </p>
                      </div>
                    ) : (
                      /* Upload Video Input */
                      <label className="flex flex-col items-center justify-center h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors">
                        <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={uploadingVideo} />
                        {uploadingVideo ? (
                          <div className="text-center">
                            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                            <p className="text-sm text-foreground">{Math.round(uploadProgress)}%</p>
                            {uploadMessage && <p className="text-xs text-muted-foreground mt-1">{uploadMessage}</p>}
                          </div>
                        ) : (
                          <>
                            <Video className="w-8 h-8 text-muted-foreground mb-2" />
                            <span className="text-sm text-muted-foreground">
                              {language === 'nl' ? 'Tik om video te uploaden' : 'Tap to upload video'}
                            </span>
                            <span className="text-xs text-muted-foreground mt-1">Max 100MB, 2 min</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>

                  {/* AI Text Import - Only show for "sale" type, when not editing and no data filled yet */}
                  {formData.listing_type === 'sale' && !isEditing && !formData.title && !importSuccess && importsRemaining > 0 && aiImportEnabled && (
                    <div className="p-4 sm:p-5 bg-gradient-to-r from-violet-500/10 via-purple-500/5 to-transparent rounded-2xl border border-violet-500/20">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground text-sm sm:text-base">
                            {language === 'nl' ? 'Staat deze advertentie al op Facebook?' : language === 'es' ? '¿Ya tienes este anuncio en Facebook?' : 'Is this listing already on Facebook?'}
                          </h3>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {language === 'nl' 
                              ? 'Kopieer de titel en beschrijving en plak hier. AI vult de rest in.'
                              : language === 'es'
                                ? 'Copia el título y la descripción y pégalo aquí. La IA completa el resto.'
                                : 'Copy the title and description and paste here. AI fills in the rest.'}
                          </p>
                        </div>
                        <div className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded-lg">
                          {importsRemaining}/5
                        </div>
                      </div>
                      
                      {/* Text input area */}
                      <div className="space-y-3">
                        <div className="relative">
                          <textarea
                            value={importText}
                            onChange={(e) => setImportText(e.target.value)}
                            placeholder={language === 'nl' 
                              ? 'Plak hier de tekst van de Facebook advertentie...\n\nBijvoorbeeld:\niPhone 12 Pro 128GB\nNAf 450\nGoed werkende telefoon, kleine krasjes op de achterkant. Inclusief oplader.'
                              : 'Paste the Facebook ad text here...\n\nExample:\niPhone 12 Pro 128GB\n$450\nGood working phone, minor scratches on back. Includes charger.'}
                            className="w-full h-28 px-3 py-2.5 text-sm bg-background border border-violet-500/30 rounded-xl resize-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/50 outline-none placeholder:text-muted-foreground/50"
                            disabled={importing}
                            data-testid="text-import-input"
                          />
                          {importText && (
                            <button
                              type="button"
                              onClick={clearImportText}
                              className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Import button */}
                        <button
                          type="button"
                          onClick={handleTextImport}
                          disabled={importing || !importText.trim()}
                          className={`w-full px-4 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                            importing
                              ? 'bg-violet-500/50 text-white cursor-wait'
                              : importText.trim()
                                ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700'
                                : 'bg-secondary text-muted-foreground cursor-not-allowed'
                          }`}
                          data-testid="text-import-btn"
                        >
                          {importing ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              {language === 'nl' ? 'AI analyseert...' : 'AI analyzing...'}
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-5 h-5" />
                              {language === 'nl' ? 'Importeer met AI' : 'Import with AI'}
                            </>
                          )}
                        </button>
                        
                        {/* Help text */}
                        <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
                          <Shield className="w-3 h-3" />
                          {language === 'nl' 
                            ? 'AI herkent: titel, prijs, categorie, staat en kenmerken'
                            : 'AI detects: title, price, category, condition and attributes'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Success message after import */}
                  {importSuccess && (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-500/10 rounded-xl p-4">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <span>
                        {language === 'nl' 
                          ? 'Gegevens geïmporteerd! Controleer en pas aan waar nodig.'
                          : 'Data imported! Review and adjust as needed.'}
                      </span>
                    </div>
                  )}

                  {/* Title field - FIRST for better UX */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium text-foreground">
                        {language === 'nl' ? 'Titel *' : 'Title *'}
                      </label>
                      <span className={`text-xs ${formData.title.length > 55 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                        {formData.title.length}/60
                      </span>
                    </div>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      maxLength={60}
                      placeholder={language === 'nl' ? 'bijv. Toyota Corolla 2020' : 'e.g., 2020 Toyota Corolla'}
                      className="w-full px-4 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="title-input"
                    />
                  </div>

                  {/* CATEGORY DROPDOWN - Safari compatible */}
                  <div className="relative" style={{ zIndex: 100, overflow: 'visible' }}>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {language === 'nl' ? 'Categorie *' : language === 'es' ? 'Categoría *' : 'Category *'}
                    </label>
                    <SafariDropdown
                      value={formData.category}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, category: value, subcategory: '' }));
                      }}
                      options={(allowedCategories.length > 0 ? allowedCategories : categories).map(cat => ({
                        value: cat.id,
                        label: cat.name
                      }))}
                      placeholder={language === 'nl' ? 'Selecteer categorie' : language === 'es' ? 'Seleccionar categoría' : 'Select category'}
                      testId="category-select"
                    />
                  </div>

                  {/* Subcategory - Safari compatible */}
                  {selectedCategory && selectedCategory.subcategories?.length > 0 && (
                    <div className="relative" style={{ zIndex: 90, overflow: 'visible' }}>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        {language === 'nl' ? 'Subcategorie *' : language === 'es' ? 'Subcategoría *' : 'Subcategory *'}
                      </label>
                      <SafariDropdown
                        value={formData.subcategory}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, subcategory: value }));
                        }}
                        options={selectedCategory.subcategories.map(sub => {
                          const subId = typeof sub === 'object' ? sub.id : sub;
                          const subName = typeof sub === 'object' ? sub.name : sub;
                          return {
                            value: subId,
                            label: getSubcategoryTranslation(subName, language)
                          };
                        })}
                        placeholder={language === 'nl' ? 'Selecteer subcategorie' : language === 'es' ? 'Seleccionar subcategoría' : 'Select subcategory'}
                        testId="subcategory-select"
                      />
                    </div>
                  )}

                  {/* Description - MOVED UP */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {language === 'nl' ? 'Beschrijving *' : 'Description *'}
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder={language === 'nl' ? 'Beschrijf je artikel in detail...' : 'Describe your item in detail...'}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                      data-testid="description-input"
                    />
                    <div className="flex justify-between mt-1">
                      <p className={`text-xs ${formData.description.length >= 10 ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {formData.description.length >= 10 ? (
                          <span className="flex items-center gap-1">
                            <Check className="w-3 h-3" /> 
                            {language === 'nl' ? 'Goede beschrijving' : 'Good description'}
                          </span>
                        ) : (
                          `${formData.description.length}/10 ${language === 'nl' ? 'minimaal' : 'minimum'}`
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{formData.description.length}/5000</p>
                    </div>
                  </div>

                  {/* Island Selector - Single Selection */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Locatie' : 'Location'}
                    </h3>
                    <label className="block text-sm font-medium text-muted-foreground mb-3">
                      {language === 'nl' ? 'Op welk eiland bevindt dit zich?' : 'Where is this item located?'}
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(ISLAND_FLAGS).map(([id, { flag, name }]) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => handleIslandSelect(id)}
                          className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                            selectedIslands.includes(id)
                              ? 'border-primary bg-primary/10 text-primary shadow-sm'
                              : 'border-border hover:border-primary/50 hover:bg-secondary/50 text-foreground'
                          }`}
                          data-testid={`island-${id}`}
                        >
                          <span className="text-3xl">{flag}</span>
                          <span className="text-sm font-medium">{name}</span>
                        </button>
                      ))}
                    </div>
                    {selectedIslands.length > 0 && (
                      <p className="text-xs text-green-500 mt-3 flex items-center gap-1">
                        <Check className="w-3 h-3" /> {ISLAND_FLAGS[selectedIslands[0]]?.name || selectedIslands[0]} {language === 'nl' ? 'geselecteerd' : 'selected'}
                      </p>
                    )}
                  </div>

                  {/* === SECTION: CATEGORY DETAILS - Extra top margin for visual separation === */}
                  {categoryProperties.length > 0 && formData.listing_type !== 'yard_sale' && (
                    <div className="bg-card rounded-2xl border border-border p-4 sm:p-6 mt-8">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        {language === 'nl' ? 'Categorie Details' : 'Category Details'}
                      </h3>
                      {loadingProperties ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-6 h-6 text-primary animate-spin" />
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Group properties by their group field */}
                          {Object.entries(
                            categoryProperties
                              .filter(prop => {
                                // Handle depends_on logic - hide property if dependency not met
                                if (prop.depends_on) {
                                  const dep = prop.depends_on;
                                  const depField = dep.property_key || dep.field || dep.key;
                                  const depValue = dep.value;
                                  
                                  if (!depField) return true;
                                  
                                  let currentValue = null;
                                  if (customProperties[depField] !== undefined) {
                                    currentValue = customProperties[depField];
                                  }
                                  if (currentValue === null) {
                                    const matchingKey = Object.keys(customProperties).find(k => 
                                      k.toLowerCase().startsWith(depField.toLowerCase()) ||
                                      k.toLowerCase().includes(depField.toLowerCase() + '/') ||
                                      k.toLowerCase().includes('/' + depField.toLowerCase())
                                    );
                                    if (matchingKey) {
                                      currentValue = customProperties[matchingKey];
                                    }
                                  }
                                  if (currentValue === null) {
                                    const matchingKey = Object.keys(customProperties).find(k => 
                                      depField.toLowerCase().includes(k.toLowerCase())
                                    );
                                    if (matchingKey) {
                                      currentValue = customProperties[matchingKey];
                                    }
                                  }
                                  if (currentValue === null || currentValue === undefined || currentValue === '') {
                                    return false;
                                  }
                                  if (depValue && currentValue !== depValue) {
                                    return false;
                                  }
                                }
                                return true;
                              })
                              .reduce((groups, prop) => {
                                const group = prop.group || 'Other';
                                if (!groups[group]) groups[group] = [];
                                groups[group].push(prop);
                                return groups;
                              }, {})
                          ).map(([groupName, props]) => (
                            <div key={groupName}>
                              {groupName !== 'Other' && (
                                <h4 className="text-sm font-medium text-muted-foreground mb-3">{groupName}</h4>
                              )}
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {props.map(prop => (
                                  <div key={prop.key} className={prop.type === 'boolean' ? 'col-span-1' : ''}>
                                    <label className="block text-sm font-medium text-foreground mb-1.5">
                                      {prop.name}{prop.required && ' *'}
                                      {prop.unit && <span className="text-muted-foreground ml-1">({prop.unit})</span>}
                                    </label>
                                    
                                    {prop.type === 'text' && (
                                      <>
                                        <input
                                          type="text"
                                          value={customProperties[prop.key] || ''}
                                          onChange={(e) => handleCustomPropertyChange(prop.key, e.target.value)}
                                          placeholder={prop.placeholder || ''}
                                          className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none transition-all"
                                          data-testid={`property-${prop.key}`}
                                        />
                                        {prop.description && (
                                          <p className="text-xs text-muted-foreground mt-1">{prop.description}</p>
                                        )}
                                      </>
                                    )}
                                    
                                    {prop.type === 'number' && (
                                      <input
                                        type="number"
                                        value={customProperties[prop.key] || ''}
                                        onChange={(e) => handleCustomPropertyChange(prop.key, e.target.value)}
                                        min={prop.validation?.min}
                                        max={prop.validation?.max}
                                        step={prop.validation?.step || 1}
                                        placeholder={prop.placeholder || ''}
                                        className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none transition-all"
                                        data-testid={`property-${prop.key}`}
                                      />
                                    )}
                                    
                                    {prop.type === 'select' && (
                                      <SafariDropdown
                                        value={customProperties[prop.key] || ''}
                                        onChange={(value) => handleCustomPropertyChange(prop.key, value)}
                                        options={(prop.options || []).map(opt => ({
                                          value: typeof opt === 'object' ? opt.value : opt,
                                          label: typeof opt === 'object' ? opt.label : opt
                                        }))}
                                        placeholder={prop.placeholder || `Select ${prop.name}`}
                                        testId={`property-${prop.key}`}
                                      />
                                    )}
                                    
                                    {prop.type === 'predefined_list' && (
                                      <SafariDropdown
                                        value={customProperties[prop.key] || ''}
                                        onChange={(value) => handleCustomPropertyChange(prop.key, value)}
                                        options={(prop.options || []).map(opt => ({
                                          value: typeof opt === 'object' ? opt.value : opt,
                                          label: typeof opt === 'object' ? opt.label : opt
                                        }))}
                                        placeholder={prop.placeholder || `Select...`}
                                        testId={`property-${prop.key}`}
                                      />
                                    )}
                                    
                                    {prop.type === 'boolean' && (
                                      <label className="flex items-center gap-3 cursor-pointer mt-2 group">
                                        <div className="relative">
                                          <input
                                            type="checkbox"
                                            checked={customProperties[prop.key] || false}
                                            onChange={(e) => handleCustomPropertyChange(prop.key, e.target.checked)}
                                            className="sr-only peer"
                                            data-testid={`property-${prop.key}`}
                                          />
                                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
                                            ${customProperties[prop.key] 
                                              ? 'bg-primary border-primary' 
                                              : 'border-border bg-card group-hover:border-primary/50'
                                            }`}
                                          >
                                            {customProperties[prop.key] && (
                                              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                              </svg>
                                            )}
                                          </div>
                                        </div>
                                        <span className={`text-sm transition-colors ${customProperties[prop.key] ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
                                          {language === 'nl' ? 'Ja' : 'Yes'}
                                        </span>
                                      </label>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* === SECTION: FEATURES - Moved here for better UX === */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4">
                      {language === 'nl' ? 'Kenmerken (Optioneel)' : 'Features (Optional)'}
                    </h3>
                    
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                        placeholder={language === 'nl' ? 'bijv. Airco, Nieuw, Garantie' : 'e.g., Air Conditioning, New, Warranty'}
                        className="flex-1 px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm"
                        data-testid="feature-input"
                      />
                      <button type="button" onClick={addFeature} className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {formData.features.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.features.map((feature, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-xl text-sm">
                            {feature}
                            <button type="button" onClick={() => removeFeature(idx)} className="hover:text-red-500 transition-colors">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* === SECTION: PRICING === */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Prijs' : 'Price'}
                    </h3>

              {/* ============== PRICING CONTENT FROM OLD DATA SECTION ============== */}

                  {/* === YARD SALE: Inventory Builder === */}
                  {formData.listing_type === 'yard_sale' && (
                    <div className="space-y-6">
                      <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                          <ShoppingBag className="w-5 h-5 text-primary" />
                          {language === 'nl' ? 'Voeg artikelen toe aan je verkoop' : 'Add Items to Your Sale'}
                        </h3>
                        
                        {/* Global Currency Selector */}
                        <div className="mb-4 p-3 bg-muted/30 rounded-xl border border-border">
                          <label className="block text-sm font-medium text-foreground mb-2">
                            {language === 'nl' ? 'Valuta voor alle artikelen' : 'Currency for all items'}
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, yard_sale: { ...prev.yard_sale, currency: 'USD' } }))}
                              className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                                formData.yard_sale?.currency === 'USD' 
                                  ? 'bg-primary text-white' 
                                  : 'bg-card border border-border text-foreground hover:border-primary/50'
                              }`}
                            >
                              USD ($)
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, yard_sale: { ...prev.yard_sale, currency: 'XCG' } }))}
                              className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                                formData.yard_sale?.currency === 'XCG' 
                                  ? 'bg-primary text-white' 
                                  : 'bg-card border border-border text-foreground hover:border-primary/50'
                              }`}
                            >
                              XCG
                            </button>
                          </div>
                        </div>
                        
                        {/* Item List with Total */}
                        {formData.yard_sale?.items?.length > 0 && (
                          <>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                              {formData.yard_sale.items.map((item, idx) => (
                                <div 
                                  key={idx} 
                                  className={`relative group bg-secondary/50 rounded-xl p-3 border transition-colors ${
                                    editingItemIndex === idx ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                                  }`}
                                >
                                  {item.image ? (
                                    <img src={item.image} alt={item.name} className="w-full h-20 object-cover rounded-lg mb-2" />
                                  ) : (
                                    <div className="w-full h-20 bg-muted rounded-lg mb-2 flex items-center justify-center">
                                      <Package className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                  )}
                                  <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs text-primary font-semibold">
                                      {formData.yard_sale?.currency === 'XCG' ? 'XCG ' : '$'}{item.price?.toLocaleString() || item.price}
                                      {(item.quantity || 1) > 1 && (
                                        <span className="text-muted-foreground ml-1">x{item.quantity}</span>
                                      )}
                                    </p>
                                    {(item.quantity || 1) > 1 && (
                                      <p className="text-xs text-muted-foreground">
                                        = {formData.yard_sale?.currency === 'XCG' ? 'XCG ' : '$'}{((item.price || 0) * (item.quantity || 1)).toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {/* Edit & Delete buttons */}
                                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      type="button"
                                      onClick={() => editYardSaleItem(idx)}
                                      className="p-1 bg-primary text-white rounded-lg hover:bg-primary/80"
                                      title={language === 'nl' ? 'Bewerken' : 'Edit'}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeYardSaleItem(idx)}
                                      className="p-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                      title={language === 'nl' ? 'Verwijderen' : 'Delete'}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Total Price Display - includes quantity */}
                            <div className="p-3 bg-primary/10 rounded-xl border border-primary/30 mb-4">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-foreground">
                                  {language === 'nl' ? 'Totaal' : 'Total'} ({formData.yard_sale.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 1), 0)} {language === 'nl' ? 'artikelen' : 'items'})
                                </span>
                                <span className="text-lg font-bold text-primary">
                                  {formData.yard_sale?.currency === 'XCG' ? 'XCG ' : '$'}
                                  {formData.yard_sale.items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {/* Add New Item Form - Compact Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {/* Image Upload */}
                          <div className="col-span-1 sm:col-span-2 md:col-span-1">
                            <label className="block w-full aspect-square max-w-[200px] mx-auto sm:max-w-none rounded-xl border-2 border-dashed border-border hover:border-primary/50 cursor-pointer transition-colors overflow-hidden">
                              <input type="file" accept="image/*" className="hidden" onChange={handleYardSaleItemImageUpload} />
                              {yardSaleItemUploading ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                </div>
                              ) : newYardSaleItem.image ? (
                                <img src={newYardSaleItem.image} alt="Preview" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center">
                                  <Camera className="w-8 h-8 text-muted-foreground mb-1" />
                                  <span className="text-xs text-muted-foreground">{language === 'nl' ? 'Foto' : 'Photo'}</span>
                                </div>
                              )}
                            </label>
                          </div>
                          
                          {/* Name */}
                          <div className="col-span-1 sm:col-span-2">
                            <input
                              type="text"
                              value={newYardSaleItem.name}
                              onChange={(e) => setNewYardSaleItem(prev => ({ ...prev, name: e.target.value }))}
                              placeholder={language === 'nl' ? 'Artikelnaam *' : 'Item name *'}
                              className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                              data-testid="yard-item-name"
                            />
                          </div>
                          
                          {/* Price (no currency selector - uses global) */}
                          <div className="col-span-1">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                {formData.yard_sale?.currency === 'XCG' ? 'XCG' : '$'}
                              </span>
                              <input
                                type="number"
                                value={newYardSaleItem.price}
                                onChange={(e) => setNewYardSaleItem(prev => ({ ...prev, price: e.target.value }))}
                                placeholder={language === 'nl' ? 'Prijs *' : 'Price *'}
                                className="w-full pl-12 pr-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                data-testid="yard-item-price"
                              />
                            </div>
                          </div>
                          
                          {/* Quantity */}
                          <div className="col-span-1">
                            <input
                              type="number"
                              value={newYardSaleItem.quantity}
                              onChange={(e) => setNewYardSaleItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                              placeholder={language === 'nl' ? 'Aantal' : 'Qty'}
                              min="1"
                              className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                              data-testid="yard-item-quantity"
                            />
                          </div>
                          
                          {/* Add/Save Button with Cancel option when editing */}
                          <div className="col-span-1 sm:col-span-2 md:col-span-3">
                            {editingItemIndex !== null ? (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={saveEditedYardSaleItem}
                                  className="flex-1 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                                >
                                  <Check className="w-4 h-4" />
                                  {language === 'nl' ? 'Opslaan' : 'Save Changes'}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditItem}
                                  className="px-4 py-2.5 bg-muted text-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors flex items-center justify-center gap-2"
                                >
                                  <X className="w-4 h-4" />
                                  {language === 'nl' ? 'Annuleren' : 'Cancel'}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={addYardSaleItem}
                                className="w-full py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                                data-testid="add-yard-item-btn"
                              >
                                <Plus className="w-4 h-4" />
                                {language === 'nl' ? 'Artikel Toevoegen' : 'Add Item'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Bundle Options */}
                      {formData.yard_sale?.items?.length > 0 && (
                        <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                          <h3 className="font-semibold text-foreground mb-4">
                            {language === 'nl' ? 'Verkoop Opties' : language === 'es' ? 'Opciones de Venta' : 'Sale Options'}
                          </h3>
                          
                          {/* Bundle Deal Toggle */}
                          <div 
                            className="flex items-center gap-3 cursor-pointer group mb-4"
                            onClick={() => setFormData(prev => ({ 
                              ...prev, 
                              yard_sale: { 
                                ...prev.yard_sale, 
                                enable_bundle_deal: !prev.yard_sale?.enable_bundle_deal
                              }
                            }))}
                          >
                            <div 
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                formData.yard_sale?.enable_bundle_deal 
                                  ? 'bg-primary border-primary' 
                                  : 'bg-transparent border-muted-foreground/50 group-hover:border-primary/50'
                              }`}
                            >
                              {formData.yard_sale?.enable_bundle_deal && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <span className={`text-sm font-medium transition-colors ${formData.yard_sale?.enable_bundle_deal ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                                {language === 'nl' ? 'Bied bundeldeal aan' : language === 'es' ? 'Ofrecer paquete' : 'Offer bundle deal'}
                              </span>
                              <p className="text-xs text-muted-foreground">
                                {language === 'nl' ? 'Korting als alles samen wordt gekocht' : language === 'es' ? 'Descuento si se compra todo junto' : 'Discount when buying everything together'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Bundle Discount Options - Only shown when toggle is ON */}
                          {formData.yard_sale?.enable_bundle_deal && (() => {
                            const totalPrice = formData.yard_sale.items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 1)), 0);
                            const discountType = formData.yard_sale?.bundle_discount_type || 'percentage';
                            const discountValue = parseFloat(formData.yard_sale?.bundle_discount_value) || 0;
                            const currencySymbol = formData.yard_sale?.currency === 'XCG' ? 'XCG ' : '$';
                            
                            let bundlePrice = totalPrice;
                            let savings = 0;
                            if (discountType === 'percentage' && discountValue > 0) {
                              savings = totalPrice * (discountValue / 100);
                              bundlePrice = totalPrice - savings;
                            } else if (discountType === 'amount' && discountValue > 0) {
                              savings = discountValue;
                              bundlePrice = totalPrice - discountValue;
                            }
                            
                            return (
                              <div className="p-4 bg-muted/30 rounded-xl border border-border mb-4 space-y-4">
                                {/* Discount Type Selection */}
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-2">
                                    {language === 'nl' ? 'Kortingstype' : 'Discount type'}
                                  </label>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setFormData(prev => ({ 
                                        ...prev, 
                                        yard_sale: { ...prev.yard_sale, bundle_discount_type: 'percentage', bundle_discount_value: '' }
                                      }))}
                                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                                        discountType === 'percentage'
                                          ? 'bg-primary text-white' 
                                          : 'bg-card border border-border text-foreground hover:border-primary/50'
                                      }`}
                                    >
                                      {language === 'nl' ? 'Percentage (%)' : 'Percentage (%)'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setFormData(prev => ({ 
                                        ...prev, 
                                        yard_sale: { ...prev.yard_sale, bundle_discount_type: 'amount', bundle_discount_value: '' }
                                      }))}
                                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                                        discountType === 'amount'
                                          ? 'bg-primary text-white' 
                                          : 'bg-card border border-border text-foreground hover:border-primary/50'
                                      }`}
                                    >
                                      {language === 'nl' ? 'Vast bedrag' : 'Fixed amount'}
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Discount Value Input */}
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1.5">
                                    {discountType === 'percentage' 
                                      ? (language === 'nl' ? 'Korting in %' : 'Discount %')
                                      : (language === 'nl' ? 'Kortingsbedrag' : 'Discount amount')
                                    }
                                  </label>
                                  <div className="relative">
                                    {discountType === 'amount' && (
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                                        {currencySymbol}
                                      </span>
                                    )}
                                    <input
                                      type="number"
                                      value={formData.yard_sale?.bundle_discount_value || ''}
                                      onChange={(e) => setFormData(prev => ({ 
                                        ...prev, 
                                        yard_sale: { ...prev.yard_sale, bundle_discount_value: e.target.value }
                                      }))}
                                      placeholder={discountType === 'percentage' ? (language === 'nl' ? 'bijv. 15' : 'e.g., 15') : (language === 'nl' ? 'bijv. 100' : 'e.g., 100')}
                                      className={`w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none ${discountType === 'amount' ? 'pl-12' : ''}`}
                                      max={discountType === 'percentage' ? 100 : totalPrice}
                                    />
                                    {discountType === 'percentage' && (
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Price Calculation Preview */}
                                {discountValue > 0 && (
                                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                                    <div className="flex justify-between items-center text-sm mb-1">
                                      <span className="text-muted-foreground">{language === 'nl' ? 'Originele prijs' : 'Original price'}:</span>
                                      <span className="text-foreground line-through">{currencySymbol}{totalPrice.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm mb-1">
                                      <span className="text-muted-foreground">{language === 'nl' ? 'Korting' : 'Discount'}:</span>
                                      <span className="text-emerald-500">-{currencySymbol}{savings.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-2 border-t border-emerald-500/30">
                                      <span className="font-semibold text-foreground">{language === 'nl' ? 'Bundelprijs' : 'Bundle price'}:</span>
                                      <span className="text-lg font-bold text-emerald-500">{currencySymbol}{bundlePrice.toLocaleString()}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          
                          {/* Other Options - Always visible */}
                          <div className="space-y-3 pt-3 border-t border-border">
                            <label className="flex items-center gap-3 cursor-pointer group">
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  name="yard_sale.allow_individual_purchase"
                                  checked={formData.yard_sale?.allow_individual_purchase !== false}
                                  onChange={handleChange}
                                  className="sr-only peer"
                                />
                                <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
                                  ${formData.yard_sale?.allow_individual_purchase !== false 
                                    ? 'bg-primary border-primary' 
                                    : 'border-border bg-card group-hover:border-primary/50'
                                  }`}
                                >
                                  {formData.yard_sale?.allow_individual_purchase !== false && (
                                    <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <span className={`text-sm transition-colors ${formData.yard_sale?.allow_individual_purchase !== false ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
                                {language === 'nl' ? 'Sta individuele aankopen toe' : language === 'es' ? 'Permitir compras individuales' : 'Allow individual item purchases'}
                              </span>
                            </label>
                            <div 
                              className="flex items-center gap-3 cursor-pointer"
                              onClick={() => setFormData(prev => ({ 
                                ...prev, 
                                yard_sale: { 
                                  ...prev.yard_sale, 
                                  enable_whatsapp_per_item: !(prev.yard_sale?.enable_whatsapp_per_item !== false)
                                }
                              }))}
                            >
                              <div 
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                  formData.yard_sale?.enable_whatsapp_per_item !== false 
                                    ? 'bg-primary border-primary' 
                                    : 'bg-transparent border-muted-foreground/50'
                                }`}
                              >
                                {formData.yard_sale?.enable_whatsapp_per_item !== false && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm text-foreground select-none">
                                {language === 'nl' ? 'WhatsApp aanvraag per artikel inschakelen' : language === 'es' ? 'Habilitar consulta por WhatsApp por artículo' : 'Enable WhatsApp inquiry per item'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* === AUCTION: Starting Bid, Reserve, End Date === */}
                  {formData.listing_type === 'auction' && (
                    <div className="bg-card rounded-2xl border border-border p-4 sm:p-6 space-y-4">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <Gavel className="w-5 h-5 text-primary" />
                        {language === 'nl' ? 'Veiling Instellingen' : 'Auction Settings'}
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Row 1: Starting Bid with Currency */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">
                            {language === 'nl' ? 'Startbod *' : 'Starting Bid *'}
                          </label>
                          <div className="flex gap-2 max-w-xs">
                            <input
                              type="number"
                              name="auction.starting_price"
                              value={formData.auction?.starting_price || ''}
                              onChange={handleChange}
                              placeholder="0.00"
                              className="flex-1 px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                              data-testid="auction-starting-price"
                            />
                            <SafariDropdown
                              value={formData.pricing?.currency || 'USD'}
                              onChange={(value) => setFormData(prev => ({
                                ...prev,
                                pricing: { ...prev.pricing, currency: value }
                              }))}
                              options={[
                                { value: 'USD', label: 'USD' },
                                { value: 'XCG', label: 'XCG' }
                              ]}
                              placeholder="USD"
                              testId="auction-currency-select"
                              className="w-24"
                            />
                          </div>
                        </div>
                        
                        {/* Row 2: Reserve Price & Bid Increment */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              {language === 'nl' ? 'Minimumprijs' : 'Reserve Price'}
                            </label>
                            <input
                              type="number"
                              name="auction.reserve_price"
                              value={formData.auction?.reserve_price || ''}
                              onChange={handleChange}
                              placeholder={language === 'nl' ? 'Optioneel' : 'Optional'}
                              className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              {language === 'nl' ? 'Bod Verhoging' : 'Bid Increment'}
                            </label>
                            <input
                              type="number"
                              name="auction.bid_increment"
                              value={formData.auction?.bid_increment || ''}
                              onChange={handleChange}
                              placeholder="1"
                              className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                            />
                          </div>
                        </div>
                        
                        {/* Row 3: End Date */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">
                            {language === 'nl' ? 'Veiling Einddatum *' : 'Auction End Date *'}
                          </label>
                          <input
                            type="datetime-local"
                            name="auction.auction_end_time"
                            value={formData.auction?.auction_end_time || ''}
                            onChange={handleChange}
                            min={new Date().toISOString().slice(0, 16)}
                            className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                            data-testid="auction-end-time"
                          />
                        </div>
                      </div>
                      
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            name="auction.auto_extend"
                            checked={formData.auction?.auto_extend !== false}
                            onChange={handleChange}
                            className="sr-only peer"
                          />
                          <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
                            ${formData.auction?.auto_extend !== false 
                              ? 'bg-primary border-primary' 
                              : 'border-border bg-card group-hover:border-primary/50'
                            }`}
                          >
                            {formData.auction?.auto_extend !== false && (
                              <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <span className={`text-sm transition-colors ${formData.auction?.auto_extend !== false ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
                          {language === 'nl' ? 'Automatisch verlengen bij bod in laatste 5 minuten' : 'Auto-extend if bid placed in last 5 minutes'}
                        </span>
                      </label>
                    </div>
                  )}

                  {/* === TRADE: What you want === */}
                  {formData.listing_type === 'trade' && (
                    <div className="bg-card rounded-2xl border border-border p-4 sm:p-6 space-y-4">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">
                        <Repeat className="w-5 h-5 text-primary" />
                        {language === 'nl' ? 'Ruil Voorkeuren' : 'Trade Preferences'}
                      </h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {language === 'nl' ? 'Waar ben je naar op zoek? *' : 'What are you looking for? *'}
                        </label>
                        <textarea
                          name="trade.wanted_items"
                          value={formData.trade?.wanted_items || ''}
                          onChange={handleChange}
                          placeholder={language === 'nl' ? 'Beschrijf waarvoor je wilt ruilen...' : "Describe what you'd trade for..."}
                          rows={3}
                          className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none resize-none"
                          data-testid="trade-wanted-items"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">
                            {language === 'nl' ? 'Geschatte Waarde' : 'Estimated Value'}
                          </label>
                          <input
                            type="number"
                            name="trade.trade_value"
                            value={formData.trade?.trade_value || ''}
                            onChange={handleChange}
                            placeholder={language === 'nl' ? 'Optioneel' : 'Optional'}
                            className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-3 cursor-pointer group">
                            <div className="relative">
                              <input
                                type="checkbox"
                                name="trade.willing_to_add_cash"
                                checked={formData.trade?.willing_to_add_cash || false}
                                onChange={handleChange}
                                className="sr-only peer"
                              />
                              <div className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center
                                ${formData.trade?.willing_to_add_cash 
                                  ? 'bg-primary border-primary' 
                                  : 'border-border bg-card group-hover:border-primary/50'
                                }`}
                              >
                                {formData.trade?.willing_to_add_cash && (
                                  <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            <span className={`text-sm transition-colors ${formData.trade?.willing_to_add_cash ? 'text-primary font-medium' : 'text-foreground group-hover:text-primary'}`}>
                              {language === 'nl' ? 'Bereid om geld bij te betalen' : 'Willing to add cash'}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* === REGULAR/RENT/SERVICE: Price & Condition === */}
                  {!['yard_sale', 'auction', 'trade'].includes(formData.listing_type) && (
                    <div className="space-y-4">
                        {/* Bidding Toggle - Only for sale type */}
                        {formData.listing_type === 'sale' && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
                            <label className="flex items-center justify-between cursor-pointer">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                                  <Gavel className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <p className="font-medium text-foreground text-sm">
                                    {language === 'nl' ? 'Bieden toestaan' : 'Allow Bidding'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {language === 'nl' ? 'Laat kopers bieden op dit artikel' : 'Let buyers place bids on this item'}
                                  </p>
                                </div>
                              </div>
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  name="pricing.bidding_allowed"
                                  checked={formData.pricing?.bidding_allowed || false}
                                  onChange={handleChange}
                                  className="sr-only peer"
                                  data-testid="bidding-allowed-toggle"
                                />
                                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-amber-500 transition-colors"></div>
                                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-5"></div>
                              </div>
                            </label>
                            
                            {/* Minimum Bid Input - Shows when bidding is enabled */}
                            {formData.pricing?.bidding_allowed && (
                              <div className="mt-4 pt-4 border-t border-amber-500/20">
                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                  {language === 'nl' ? 'Minimumbod (Optioneel)' : 'Minimum Bid (Optional)'}
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="number"
                                    name="pricing.minimum_bid"
                                    value={formData.pricing?.minimum_bid || ''}
                                    onChange={handleChange}
                                    placeholder={language === 'nl' ? 'Leeg laten voor geen minimum' : 'Leave empty for no minimum'}
                                    className="flex-1 px-3 py-2 rounded-xl border border-amber-500/30 bg-card text-foreground text-sm focus:border-amber-500 outline-none"
                                    data-testid="minimum-bid-input"
                                  />
                                  <span className="flex items-center px-3 text-sm text-muted-foreground bg-muted rounded-xl">
                                    {formData.pricing?.currency || currency || 'USD'}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1.5">
                                  {language === 'nl' 
                                    ? 'Stel een bodemprijs in. Kopers kunnen alle biedingen zien en jij kunt elk bod accepteren.'
                                    : 'Set a floor price for bids. Buyers can see all bids and you can accept any bid.'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Business CTA Type Selector - Only for verified business accounts */}
                        {user?.account_type === 'business' && user?.is_verified_business && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                                <Briefcase className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground text-sm">
                                  {language === 'nl' ? 'Zakelijke Listing Type' : 'Business Listing Type'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {language === 'nl' ? 'Kies hoe klanten contact opnemen' : 'Choose how customers reach you'}
                                </p>
                              </div>
                            </div>
                            
                            {/* CTA Type Radio Options */}
                            <div className="space-y-2">
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                  !formData.cta_type || formData.cta_type === '' 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => setFormData(prev => ({ ...prev, cta_type: '', cta_url: '' }))}
                              >
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  !formData.cta_type || formData.cta_type === '' ? 'border-primary' : 'border-muted-foreground'
                                }`}>
                                  {(!formData.cta_type || formData.cta_type === '') && (
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {language === 'nl' ? 'Standaard (Chat/WhatsApp)' : 'Standard (Chat/WhatsApp)'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {language === 'nl' ? 'Klanten kunnen direct contact opnemen via het platform' : 'Customers contact you directly through the platform'}
                                  </p>
                                </div>
                              </label>
                              
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                  formData.cta_type === 'promotion' 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => setFormData(prev => ({ ...prev, cta_type: 'promotion' }))}
                              >
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  formData.cta_type === 'promotion' ? 'border-primary' : 'border-muted-foreground'
                                }`}>
                                  {formData.cta_type === 'promotion' && (
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {language === 'nl' ? 'Actie / Aanbieding' : 'Promotion / Offer'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {language === 'nl' ? 'Link naar je webshop of actiepagina' : 'Link to your webshop or promotion page'}
                                  </p>
                                </div>
                              </label>
                              
                              <label 
                                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                  formData.cta_type === 'event' 
                                    ? 'border-primary bg-primary/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => setFormData(prev => ({ ...prev, cta_type: 'event' }))}
                              >
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  formData.cta_type === 'event' ? 'border-primary' : 'border-muted-foreground'
                                }`}>
                                  {formData.cta_type === 'event' && (
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {language === 'nl' ? 'Evenement / Sale' : 'Event / Sale'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {language === 'nl' ? 'Weekend sale, event of speciale actie' : 'Weekend sale, event or special promotion'}
                                  </p>
                                </div>
                              </label>
                            </div>
                            
                            {/* CTA URL & Campaign Dates - Show when promotion or event selected */}
                            {(formData.cta_type === 'promotion' || formData.cta_type === 'event') && (
                              <div className="mt-4 pt-4 border-t border-primary/20 space-y-4">
                                {/* CTA URL Input */}
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1.5">
                                    {language === 'nl' ? 'Externe Link (URL) *' : 'External Link (URL) *'}
                                  </label>
                                  <div className="relative">
                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <input
                                      type="url"
                                      name="cta_url"
                                      value={formData.cta_url || ''}
                                      onChange={handleChange}
                                      placeholder={language === 'nl' ? 'https://jouwshop.nl/actie' : 'https://yourshop.com/promo'}
                                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                                      data-testid="cta-url-input"
                                    />
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {language === 'nl' 
                                      ? 'Klanten worden doorgestuurd naar deze URL in plaats van chat' 
                                      : 'Customers will be directed to this URL instead of chat'}
                                  </p>
                                </div>
                                
                                {/* Campaign Period */}
                                <div>
                                  <label className="block text-sm font-medium text-foreground mb-1.5">
                                    {language === 'nl' ? 'Actieperiode (optioneel)' : 'Campaign Period (optional)'}
                                  </label>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs text-muted-foreground mb-1">
                                        {language === 'nl' ? 'Start' : 'Start'}
                                      </label>
                                      <input
                                        type="datetime-local"
                                        name="campaign_start"
                                        value={formData.campaign_start || ''}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                                        data-testid="campaign-start-input"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-muted-foreground mb-1">
                                        {language === 'nl' ? 'Einde' : 'End'}
                                      </label>
                                      <input
                                        type="datetime-local"
                                        name="campaign_end"
                                        value={formData.campaign_end || ''}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                                        data-testid="campaign-end-input"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Price Input - Optional when CTA type is set */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              {formData.pricing?.bidding_allowed 
                                ? (language === 'nl' ? 'Vraagprijs *' : 'Asking Price *')
                                : (formData.cta_type && formData.cta_type !== '')
                                  ? (language === 'nl' ? 'Prijs (optioneel)' : 'Price (optional)')
                                  : (language === 'nl' ? 'Prijs *' : 'Price *')}
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <input
                                type="number"
                                name="pricing.amount"
                                value={formData.pricing?.amount || ''}
                                onChange={handleChange}
                                placeholder={formData.cta_type ? (language === 'nl' ? 'Zie actie voor prijs' : 'See promotion for price') : '0.00'}
                                className="flex-1 min-w-[100px] px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                                data-testid="price-input"
                              />
                              <SafariDropdown
                                value={formData.pricing?.currency || currency || 'USD'}
                                onChange={(value) => setFormData(prev => ({
                                  ...prev,
                                  pricing: { ...prev.pricing, currency: value }
                                }))}
                                options={[
                                  { value: 'USD', label: 'USD' },
                                  { value: 'XCG', label: 'XCG' }
                                ]}
                                placeholder="USD"
                                testId="price-currency-select"
                                className="w-20"
                              />
                              {/* Price Unit - for verified business users on sale listings */}
                              {user?.is_verified_business && formData.listing_type === 'sale' && (
                                <SafariDropdown
                                  value={formData.pricing?.price_unit || ''}
                                  onChange={(value) => setFormData(prev => ({
                                    ...prev,
                                    pricing: { ...prev.pricing, price_unit: value || null }
                                  }))}
                                  options={[
                                    { value: '', label: language === 'nl' ? 'Totaal' : 'Total' },
                                    { value: 'm²', label: 'per m²' },
                                    { value: 'hectare', label: 'per hectare' },
                                    { value: 'stuk', label: language === 'nl' ? 'per stuk' : 'per piece' },
                                    { value: 'kg', label: 'per kg' }
                                  ]}
                                  placeholder={language === 'nl' ? 'Totaal' : 'Total'}
                                  testId="price-unit-select"
                                  className="min-w-[110px]"
                                />
                              )}
                            </div>
                            {formData.pricing?.bidding_allowed && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {language === 'nl' 
                                  ? 'Dit is jouw vraagprijs. Kopers kunnen lager of hoger bieden.'
                                  : 'This is the price you\'re asking. Buyers can bid below or above this.'}
                              </p>
                            )}
                            {(formData.cta_type === 'promotion' || formData.cta_type === 'event') && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {language === 'nl' 
                                  ? 'Prijs is optioneel voor acties. Laat leeg om "Bekijk Actie" te tonen.'
                                  : 'Price is optional for promotions. Leave empty to show "See Promotion".'}
                              </p>
                            )}
                          </div>
                          
                          {formData.listing_type === 'rent' && (
                            <div className="flex items-end">
                              <div>
                                <label className="block text-sm font-medium text-foreground mb-1.5">
                                  {language === 'nl' ? 'Periode' : language === 'es' ? 'Período' : 'Period'}
                                </label>
                                <SafariDropdown
                                  value={formData.pricing?.rental_period || 'monthly'}
                                  onChange={(value) => setFormData(prev => ({
                                    ...prev,
                                    pricing: { ...prev.pricing, rental_period: value }
                                  }))}
                                  options={[
                                    { value: 'hourly', label: language === 'nl' ? 'Per uur' : language === 'es' ? 'Por hora' : 'Per hour' },
                                    { value: 'daily', label: language === 'nl' ? 'Per dag' : language === 'es' ? 'Por día' : 'Per day' },
                                    { value: 'weekly', label: language === 'nl' ? 'Per week' : language === 'es' ? 'Por semana' : 'Per week' },
                                    { value: 'monthly', label: language === 'nl' ? 'Per maand' : language === 'es' ? 'Por mes' : 'Per month' },
                                    { value: 'yearly', label: language === 'nl' ? 'Per jaar' : language === 'es' ? 'Por año' : 'Per year' }
                                  ]}
                                  placeholder={language === 'nl' ? 'Per maand' : 'Per month'}
                                  testId="rental-period-select"
                                  className="min-w-[130px]"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                  )}

                  </div>
                  
                  {/* Navigation Buttons */}
                  <div className="flex justify-between pt-4">
                    <button type="button" onClick={prevStep} className="px-6 py-2.5 rounded-xl border border-border text-foreground font-medium hover:bg-secondary transition-colors flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      {language === 'nl' ? 'Terug' : 'Back'}
                    </button>
                    <button type="button" onClick={nextStep} className="px-6 py-2.5 rounded-xl bg-primary text-white font-medium hover:bg-primary/90 transition-colors flex items-center gap-2">
                      {language === 'nl' ? 'Volgende' : 'Next'}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* ============== STEP 4: FINALIZE ============== */}
              {currentStep === 2 && (
                <div className="space-y-6" data-testid="step-4-finalize" style={{ overflow: 'visible' }}>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      {language === 'nl' ? 'Afronden & Publiceren' : 'Finalize & Publish'}
                    </h2>
                    <p className="text-muted-foreground">
                      {language === 'nl' ? 'Controleer je advertentie en voeg contactgegevens toe' : 'Review your listing and add contact details'}
                    </p>
                  </div>

                  {/* Contact Info */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Contactgegevens' : 'Contact Information'}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {language === 'nl' ? 'Telefoonnummer' : 'Phone Number'}
                        </label>
                        <input
                          type="tel"
                          name="contact_phone"
                          value={formData.contact_phone}
                          onChange={handleChange}
                          placeholder="+599 9 123 4567"
                          className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          {language === 'nl' ? 'E-mail' : 'Email'}
                        </label>
                        <input
                          type="email"
                          name="contact_email"
                          value={formData.contact_email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full px-3 py-2 rounded-xl border border-input bg-card text-foreground text-sm focus:border-primary outline-none"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => setFormData(prev => ({ ...prev, enable_whatsapp: !prev.enable_whatsapp }))}
                      >
                        <div 
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            formData.enable_whatsapp 
                              ? 'bg-primary border-primary' 
                              : 'bg-transparent border-muted-foreground/50'
                          }`}
                        >
                          {formData.enable_whatsapp && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-foreground select-none">
                            {language === 'nl' ? 'WhatsApp vragen inschakelen' : 'Enable WhatsApp inquiries'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Picker */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Locatie (Optioneel)' : 'Location (Optional)'}
                    </h3>
                    <LocationPicker
                      language={language}
                      location={{
                        latitude: formData.location?.latitude,
                        longitude: formData.location?.longitude,
                        address: formData.location?.address,
                        city: formData.location?.city,
                        area: formData.location?.area,
                        country: formData.location?.country,
                      }}
                      displayType={formData.location?.display_type || 'exact'}
                      onLocationChange={(lat, lng) => {
                        setFormData(prev => ({
                          ...prev,
                          location: { ...prev.location, latitude: lat, longitude: lng }
                        }));
                        setLocationFetchedViaGPS(true);
                        setHasUnsavedChanges(true);
                      }}
                      onAddressChange={(addressData) => {
                        setFormData(prev => ({
                          ...prev,
                          location: {
                            ...prev.location,
                            address: addressData.address || prev.location?.address || '',
                            city: addressData.city || prev.location?.city || '',
                            area: addressData.area || prev.location?.area || '',
                            country: addressData.country || prev.location?.country || '',
                          }
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      onDisplayTypeChange={(type) => {
                        setFormData(prev => ({
                          ...prev,
                          location: { ...prev.location, display_type: type }
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      onLocationFetchSuccess={() => {
                        toast.success('Location found!');
                        setLocationFetchedViaGPS(true);
                      }}
                      onLocationFetchFail={() => {
                        toast.error('Failed to get location');
                      }}
                      compact={false}
                    />
                  </div>

                  {/* Live Preview Card */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-primary" />
                      {language === 'nl' ? 'Voorbeeld' : 'Preview'}
                    </h3>
                    
                    <div className="flex gap-4 p-4 bg-secondary/30 rounded-xl">
                      {/* Preview Image */}
                      <div className="w-24 h-24 flex-shrink-0 rounded-xl bg-muted overflow-hidden">
                        {formData.images[0] ? (
                          <img src={formData.images[0]} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      {/* Preview Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground truncate">{formData.title || (language === 'nl' ? 'Naamloze Advertentie' : 'Untitled Listing')}</h4>
                        <p className="text-sm text-muted-foreground truncate">{formData.category} • {formData.subcategory}</p>
                        <p className="text-lg font-bold text-primary mt-1">
                          {formData.listing_type === 'yard_sale' 
                            ? `${formData.yard_sale?.items?.length || 0} items`
                            : formData.listing_type === 'auction'
                            ? `${language === 'nl' ? 'Vanaf' : 'Starting'} ${formData.pricing?.currency === 'XCG' ? 'XCG' : '$'}${formData.auction?.starting_price || '0'}`
                            : formData.listing_type === 'trade'
                            ? 'Trade'
                            : formData.listing_type === 'giveaway'
                            ? (language === 'nl' ? 'Gratis' : 'Free')
                            : `${formData.pricing?.currency === 'XCG' ? 'XCG ' : '$'}${formData.pricing?.amount || '0'}`
                          }
                        </p>
                        <div className="flex gap-1 mt-2">
                          {selectedIslands.map(island => (
                            <span key={island} className="text-sm">{ISLAND_FLAGS[island]?.flag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Validation Summary */}
                  <div className="bg-card rounded-2xl border border-border p-4 sm:p-6">
                    <h3 className="font-semibold text-foreground mb-4">
                      {language === 'nl' ? 'Checklist' : 'Checklist'}
                    </h3>
                    <div className="space-y-2">
                      {[
                        { label: language === 'nl' ? 'Type geselecteerd' : 'Listing type selected', check: !!formData.listing_type },
                        { label: language === 'nl' ? 'Titel toegevoegd' : 'Title added', check: (formData.title?.length || 0) >= 3 },
                        { label: language === 'nl' ? 'Categorie & subcategorie' : 'Category & subcategory', check: !!formData.category && !!formData.subcategory },
                        { label: language === 'nl' ? 'Eiland geselecteerd' : 'Island selected', check: selectedIslands.length > 0 },
                        { label: language === 'nl' ? 'Beschrijving (10+ tekens)' : 'Description (10+ chars)', check: (formData.description?.length || 0) >= 10 },
                        { label: language === 'nl' ? 'Prijs/artikelen ingevuld' : 'Pricing/items complete', check: getStepStatus(2) === 'complete' },
                        { label: language === 'nl' ? 'Minimaal één foto' : 'At least one photo', check: (formData.images?.length || 0) > 0 },
                        { label: language === 'nl' ? 'Contactgegevens' : 'Contact info', check: !!(formData.contact_phone || formData.contact_email) },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          {item.check ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Circle className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className={`text-sm ${item.check ? 'text-foreground' : 'text-muted-foreground'}`}>{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <button type="button" onClick={prevStep} className="flex-1 px-6 py-3 rounded-xl border border-border text-foreground font-medium hover:bg-secondary transition-colors flex items-center justify-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      {language === 'nl' ? 'Terug' : 'Back'}
                    </button>
                    
                    {!isEditing && (
                      <button type="button" onClick={() => saveDraft(true)} disabled={savingDraft} className="px-6 py-3 rounded-xl border-2 border-primary/30 text-primary font-medium hover:bg-primary/10 transition-colors flex items-center justify-center gap-2">
                        {savingDraft ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        {language === 'nl' ? 'Concept Opslaan' : 'Save Draft'}
                      </button>
                    )}
                    
                    <button
                      type="submit"
                      disabled={loading || !canPublish}
                      className={`flex-1 px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                        canPublish 
                          ? 'bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20' 
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      }`}
                      data-testid="submit-listing-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {isEditing 
                            ? (language === 'nl' ? 'Bijwerken...' : 'Updating...')
                            : (language === 'nl' ? 'Publiceren...' : 'Publishing...')}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          {isEditing 
                            ? (language === 'nl' ? 'Advertentie Bijwerken' : 'Update Listing')
                            : (language === 'nl' ? 'Advertentie Publiceren' : 'Publish Listing')}
                        </>
                      )}
                    </button>
                  </div>
                  
                  {!canPublish && (
                    <p className="text-center text-sm text-amber-500 flex items-center justify-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {language === 'nl' ? 'Vul alle verplichte velden in om te publiceren' : 'Complete all required fields to publish'}
                    </p>
                  )}
                </div>
              )}

            </form>
          </div>
        </div>
      </div>

      {/* Share Success Modal */}
      <ShareSuccessModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        listingId={newListingData?.id}
        listingSlug={newListingData?.slug}
        listingTitle={newListingData?.title}
      />
    </div>
  );
};

export default CreateListingPage;
